import { requestAvatarCalibration } from '../avatar/calibration';
// VRViewer — lean VR-only world viewer
// No editor panels, no build tools, no TransformControls.
// Just the scene + locomotion + multiplayer + voice.
// Route: /vr/:worldId

import { FallbackOnError } from '../components/three/FallbackOnError';
import React, { Suspense, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { Sky, Environment, Text, useTexture } from '@react-three/drei';
import { XR, useXR, XROrigin, TeleportTarget, createXRStore } from '@react-three/xr';
import * as THREE from 'three';
import { Box3, Mesh, Vector3 } from 'three';
import {
  AlertTriangle, BookOpen, CalendarDays, Camera, Check, Globe, Image as ImageIcon, Loader,
  Eye, MessageSquare, PersonStanding, Shirt, Sparkles, Square, UserRound, UserX, Video, ZoomIn,
} from 'lucide-react';

import ChatOverlay from '../components/ChatOverlay';
import PreviewBanner, { type PreviewListing } from '../components/marketplace/PreviewBanner';
import PrivateWorldGate from '../components/PrivateWorldGate';
import { getWorldAccessState } from '../services/worldJoin';
import { VRMenuAnchor, MENU_DIST_CONTROLLERS, MENU_DIST_HANDS } from '../utils/vrMenuAnchor';
import VRUserMenuSurface from '../components/VR/VRUserMenuSurface';
import AvatarCreateModal from '../components/avatar-create/AvatarCreateModal';
import GuestbookPanel from '../components/GuestbookPanel';
import { unreadCount as guestbookUnreadCount } from '../services/guestbookService';

import { supabase } from '../config/supabase';
import { captureCanvasAsWorldThumbnail } from '../utils/captureThumbnail';
import { getStoredUserName } from '../utils/nameGenerator';
import { initKTX2Loader } from '../utils/loaderConfig';
import { useWorldStore } from '../stores/worldStore';
import { clearWorldHistory } from '../services/worldUndo';
import { getActiveAvatar } from '../services/avatarService';
import { getChannel, getPresenceKey } from '../services/realtimeManager';
import type { PrimitiveItem, LightItem } from '../stores/worldStore';
import { useThrottledFrame } from '../hooks/useThrottledFrame';

import { MarbleWorldRenderer } from '../plugins/marble-worlds';
import ContentDisplay from '../components/ContentDisplay';
import SimpleEnvironmentModel from '../components/SimpleEnvironmentModel';
import VRDebugReadout, { vrDebugEnabled } from '../components/VR/VRDebugReadout';
import AtmosphereScene from '../components/AtmosphereScene';
import ScreenProp from '../components/ScreenProp';
import MediaPanelActions from '../components/MediaPanelActions';
import MediaPanelDialogs from '../components/MediaPanelDialogs';
import RoomScreen from '../components/RoomScreen';
import VRSharePanel, { type ShareTarget } from '../components/VR/VRSharePanel';
import VREmotePanel from '../components/VR/VREmotePanel';
import VRHelpPanel from '../components/VR/VRHelpPanel';
import { initRoomScreens, openSharePanel, useRoomScreenStore } from '../services/roomScreens';
import BrowserProp from '../components/BrowserProp';
import VRLocomotion from '../components/VR/VRLocomotion';
import { locomotionLocked } from '../components/VR/locomotionLock';
import { useArcade } from '../components/games/arcade/arcadeStore';
import VRAvatarBody from '../components/VR/VRAvatarBody';
import AvatarWithVoiceState from '../components/AvatarWithVoiceState';
import RoomScreenDock from '../components/RoomScreenDock';
import VRPerformanceManager from '../components/VR/VRPerformanceManager';
import VRFrameOptimizer from '../components/VR/VRFrameOptimizer';
import VRPreloader from '../components/VR/VRPreloader';
import VRBuildModeOptimizer from '../components/VR/VRBuildModeOptimizer';
import MultiplayerSync from '../components/MultiplayerSync';
// Safety (mute / block / report): People list in the VR menu and the HUD.
import VRPeopleTab from '../components/safety/VRPeopleTab';
import { openPeopleList } from '../components/safety/openSafety';
import { isMutedUser } from '../stores/safetyStore';
import { Users as PeopleIcon } from 'lucide-react';
import VoiceChatWrapper from '../components/VoiceChatWrapper';
import { useVoiceStore, pressVoiceButton, voiceButtonLabel, requestVoiceJoin, unlockVoiceAudio } from '../stores/voiceStore';
import VRButtonComponent from '../components/VR/VRButton';
import VRHandControls, { VR_MENU_EVENT } from '../components/VR/hands/VRHandControls';
import { vrLift, wantedLift, setSeatedMode, createTableLiftTracker, easeLift } from '../components/VR/seatedMode';
import { seatTableTop } from '../components/games/tableLayout';
import { furnitureBoxes, isFurniture, jumpBlocked, KNEE, onFurniture, pathBlocked, vrPath } from '../components/VR/vrReach';
import { clampToBounds, insideBounds, parseWorldBounds } from '../utils/worldBounds';
import { parseWorldMusic } from '../services/music/musicTypes';
import { ViewerSpeaker } from '../components/music/SpeakerModel3D';
import { MusicDockButton, useMusicHudTool, useShowMusicButton } from '../components/music/MusicHudButton';
import { ViewerPortal, PortalTraveler, usePortalArrival, useWorldPortalArrival } from '../components/portals/portals';
import { ViewerGameTable } from '../components/games/GameTable3D';
import { GameOverlay, useSeatedAtGame } from '../components/games/GameOverlay';
import { NpcTalkProximity, NpcTalkPanel } from '../components/npc/NpcTalkPanel';
import { NpcTalkVR } from '../components/npc/NpcTalkVR';
import FixedRetargetedAvatar from '../components/FixedRetargetedAvatar';
import EmoteWheel from '../components/EmoteWheel';
import SafeR3F from '../components/SafeR3F';
import { Ground } from './WorldBuilder';
import { isTouchDevice, TouchJoystick, TouchLook, takeTouchLook, touchMove, touchMoveAmount } from '../components/mobile/touchControls';
import { ViewerHudBar, useCompactHud, type HudTool } from '../components/mobile/ViewerHudBar';
// World hopping: "Go to a world" + invites (2D layer, VR panel/prompt).
import { WorldHopLayer } from '../components/worldhop/WorldHop';
import { VRWorldHopPanel, VRInvitePrompt, useVRWorldHop } from '../components/worldhop/VRWorldHop';
import { openWorldHop, currentWorldId, travelToWorld } from '../services/worldHop';
// GathrXR Home: the dashboard as a 3D space (worlds flagged settings.vr_home).
const VRHomeDashboard = React.lazy(() => import('../components/vrhome/VRHomeDashboard'));
// Owner's "Meetup link" tool: the dialog loads only when opened.
const MeetupDialog = React.lazy(() => import('../components/social/MeetupDialog'));
// Arcade cabinets: the cabinet, its runtime and the games load only in worlds that have one.
const ViewerArcadeCabinet = React.lazy(() => import('../components/games/arcade/ArcadeCabinet3D').then((m) => ({ default: m.ViewerArcadeCabinet })));
const ArcadeOverlay = React.lazy(() => import('../components/games/arcade/ArcadeOverlay'));
const ViewerLeaderboard = React.lazy(() => import('../components/games/arcade/Leaderboard3D'));
// World games (lazy: only worlds with a world-game piece load the runtime, HUD and sounds).
import { startRecordingAudio } from '../utils/recordingAudio';
const BlackthornRuntime = React.lazy(() => import('../components/games/blackthorn/BlackthornRuntime'));
const BlackthornOverlay = React.lazy(() => import('../components/games/blackthorn/BlackthornOverlay'));
const LastBiteRuntime = React.lazy(() => import('../components/games/lastbite/LastBiteRuntime'));
const LastBiteOverlay = React.lazy(() => import('../components/games/lastbite/LastBiteOverlay'));
const CarnivalRuntime = React.lazy(() => import('../components/games/carnival/CarnivalRuntime'));
const CarnivalOverlay = React.lazy(() => import('../components/games/carnival/CarnivalOverlay'));
const WorldGameRuntime = React.lazy(() => import('../components/games/worldgame/WorldGameRuntime'));
const WorldGameOverlay = React.lazy(() => import('../components/games/worldgame/WorldGameOverlay'));
// World music (lazy: audio runtime + popover load on demand).
const MusicRuntime = React.lazy(() => import('../components/music/MusicRuntime'));
const MusicOverlay = React.lazy(() => import('../components/music/MusicOverlay'));
const MusicListenerFrame = React.lazy(() => import('../components/music/MusicListenerFrame'));
const VRMusicPanel = React.lazy(() => import('../components/music/VRMusicPanel'));
import { broadcastEmote } from '../services/worldBroadcast';
// Avatar switching in play mode: wardrobe panel (2D / VR), create flow, guard.
import { AvatarSwitchLayer } from '../components/avatarswitch/AvatarSwitch';
import { VRAvatarSwitchPanel, useVRAvatarSwitch } from '../components/avatarswitch/VRAvatarSwitch';
import { AvatarSwitchGuard } from '../components/avatarswitch/AvatarSwitchGuard';
import { openAvatarPicker, useAvatarSwitch } from '../services/avatarSwitch';
import GhostAvatar from '../components/GhostAvatar';
import NpcAvatar from '../components/npc/NpcAvatar';
import { SIT_STYLES, getSitStyle, setSitStyle, SEAT_CLEARANCE, FLOOR_PERCH } from '../config/emotes';
// Installed immersive Quest app (com.gathrxr.quest): session-first start, VR sign-in.
import { isQuestImmersiveApp, isVRHomeWorld, VR_HOME_NAME, VR_HOME_WORLD_ID } from '../utils/questApp';
import { applyLiteUrls, browserTierEnv, detectQualityTier, parseLiteSettings } from '../utils/qualityTier';
import { useLiteStore, LITE_DEFAULTS } from '../stores/liteStore';
import { QuestResizeObserver } from '../utils/questImmersiveSession';
import { QuestImmersiveBridge, QuestVRAccountPanel, openQuestAccountPanel } from '../components/quest/QuestImmersiveBridge';

// ---- XR store (module-level singleton) ----
const xrStore = createXRStore({
  offerSession: 'immersive-vr',
  enterGrantedSession: ['immersive-vr'],
  // No controller grab pointer: its sphere test uses each mesh's bounding BOX,
  // so standing inside a big merged environment mesh reads as a 0 m "grab hit",
  // the combined pointer prefers it, and the laser ray is switched off. The grip
  // (squeeze) is owned by VRGrab, which reads the gamepad and does its own tests.
  // The laser was drawn at most 1 m long, so pointing at a sign a few metres
  // away looked like it didn't reach. Draw it to the hit (up to 10 m).
  // The library ignores hits closer than 20 cm, so a controller held up near
  // the menu shot straight through it to the wall behind; 3 cm still clears
  // the controller model itself.
  controller: { grabPointer: false, rayPointer: { minDistance: 0.03, rayModel: { maxLength: 10 } } },
  // Hands: the library draws the hand laser only 20 cm long, so it never
  // looked like it reached the menu. Same 10 m to the hit as controllers, and
  // no grab sphere for the same big-mesh reason. Poking (touch) stays on.
  hand: { grabPointer: false, rayPointer: { rayModel: { maxLength: 10 } } },
  customSessionInit: {
    requiredFeatures: ['local-floor'],
    optionalFeatures: ['hand-tracking'],
  },
});

// ---- KTX2 texture loader initializer ----
const KTX2Init: React.FC = () => {
  const { gl } = useThree();
  useEffect(() => { try { initKTX2Loader(gl); } catch (_) {} }, [gl]);
  return null;
};

// ---- Prevent Quest 3 passthrough bleed when no skybox ----
const VRBackgroundEnforcer: React.FC = () => {
  const isPresenting = useXR((s) => !!s.session);
  const { scene } = useThree();
  useEffect(() => {
    if (isPresenting && !scene.background) {
      scene.background = new THREE.Color(0x000000);
      return () => { scene.background = null; };
    }
  }, [isPresenting, scene]);
  return null;
};

// ---- VR seating ----
// Shared between the menu (Sit button), the rig (teleport onto a seat) and VR
// collision (paused while seated). Head pose is reference-space, i.e. local to
// the XR origin, refreshed every frame by the menu.
const vrHead = { pos: new THREE.Vector3(0, 1.6, 0), quat: new THREE.Quaternion() };
const vrSeat: { current: { x: number; z: number; seat: FoundSeat } | null } = { current: null };
// Standing hip height for floor/squat placement in VR (the VR body has no
// measured hips the way the desktop avatar does).
const VR_HIP_HEIGHT = 0.9;

/** tableTop: world height of the game table this chair faces (none for ordinary seats). */
type FoundSeat = { x: number; z: number; surface: number; yaw: number; tableTop?: number };
const _seatP = new THREE.Vector3();
const _seatQ = new THREE.Quaternion();
/**
 * Nearest seat marker to (x, z) within maxDist, measured horizontally. With
 * `y` (a floor or surface height), only seats on that level count — in a venue
 * with a mezzanine the sofa overhead is horizontally the nearest one.
 */
function findSeat(scene: THREE.Object3D, x: number, z: number, maxDist: number, y?: number): FoundSeat | null {
  let best: THREE.Object3D | null = null;
  let bestD = maxDist;
  scene.traverse((o: any) => {
    if (!o.userData?.seat) return;
    o.getWorldPosition(_seatP);
    if (y !== undefined && Math.abs(_seatP.y - y) > 1.2) return;
    const d = Math.hypot(_seatP.x - x, _seatP.z - z);
    if (d < bestD) { bestD = d; best = o; }
  });
  if (!best) return null;
  const seat = best as THREE.Object3D;
  seat.getWorldPosition(_seatP);
  const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(seat.getWorldQuaternion(_seatQ));
  const box = new THREE.Box3().setFromObject(seat);
  return { x: _seatP.x, z: _seatP.z, surface: box.isEmpty() ? _seatP.y + 0.25 : box.max.y, yaw: Math.atan2(fwd.x, fwd.z), tableTop: seatTableTop(seat) };
}

/**
 * Seat the VR player: turn the rig so the headset faces the seat's forward,
 * slide it so the head is over the seat, and drop the origin to the same root
 * height the desktop avatar would use — remote players then see a correctly
 * seated avatar, and sitting down in real life puts your eyes at seat height.
 */
function sitVR(origin: THREE.Object3D, seat: FoundSeat, worldId: string) {
  const style = getSitStyle();
  const perch = style.emote === 'sit' ? 0.13 : FLOOR_PERCH;
  const tx = seat.x + Math.sin(seat.yaw) * perch;
  const tz = seat.z + Math.cos(seat.yaw) * perch;
  // Face the seat's forward: rotate the rig by the difference in yaw.
  const look = new THREE.Vector3(0, 0, -1).applyQuaternion(vrHead.quat);
  const headYawLocal = Math.atan2(-look.x, -look.z);
  origin.rotation.y = seat.yaw + Math.PI - headYawLocal;
  origin.updateMatrixWorld(true);
  const headW = origin.localToWorld(vrHead.pos.clone());
  origin.position.x += tx - headW.x;
  origin.position.z += tz - headW.z;
  origin.position.y = style.poseHip != null
    ? seat.surface + SEAT_CLEARANCE - style.poseHip * VR_HIP_HEIGHT
    : seat.surface - style.drop;
  origin.updateMatrixWorld(true);
  // The seat sets the rig height outright; seated-mode lift is off in a seat
  // (a game table's lift eases in from here, see ViewerVRRig).
  vrLift.applied = 0;
  vrSeat.current = { x: origin.position.x, z: origin.position.z, seat };
  useWorldStore.getState().playLocalEmote(style.emote);
  if (worldId) broadcastEmote(worldId, style.emote);
}

function standVR(worldId: string) {
  if (!vrSeat.current) return;
  vrSeat.current = null;
  useWorldStore.getState().stopLocalEmote();
  if (worldId) broadcastEmote(worldId, null);
}

// ---- VR Collision: AABB push-out against collidable meshes ----
const _colBox = new Box3();
const _chest = new THREE.Vector3();
const _kneeA = new THREE.Vector3(), _kneeB = new THREE.Vector3();
const VRCollision: React.FC<{ originRef: React.RefObject<THREE.Group | null> }> = ({ originRef }) => {
  const { scene } = useThree();
  // Same shape as the desktop avatar's cache: boxes are built on refresh, not
  // per frame, so a venue's worth of collidable meshes stays affordable in VR.
  const cache = useRef<{ mesh: Mesh; box: Box3 }[]>([]);
  const lastCache = useRef(0);
  const prevOriginY = useRef(0);
  useEffect(() => () => vrPath.reset(), []);

  useThrottledFrame(() => {
    if (!originRef.current) return;
    const now = performance.now();
    if (now - lastCache.current > 500) {
      cache.current = [];
      const NEAR = 30;
      const ox = originRef.current.position.x;
      const oz = originRef.current.position.z;
      scene.traverse((obj) => {
        if (obj.userData?.collidable !== true || !(obj as Mesh).isMesh) return;
        const box = new Box3().setFromObject(obj);
        if (box.isEmpty()) return;
        if (box.max.x < ox - NEAR || box.min.x > ox + NEAR ||
            box.max.z < oz - NEAR || box.min.z > oz + NEAR) return;
        cache.current.push({ mesh: obj as Mesh, box });
      });
      lastCache.current = now;
    }
    // Walkable bounds: whatever moved you — stick, teleport, walking in your
    // room, a snap turn swinging your head round — your head stays inside the
    // world's rectangle (settings.bounds). Open pavilions have no wall to stop
    // you; this does. Seats own the rig while you sit.
    const bounds = useWorldStore.getState().worldBounds;
    if (bounds && !vrSeat.current) {
      const o = originRef.current;
      o.updateMatrixWorld();
      const head = o.localToWorld(_chest.copy(vrHead.pos));
      if (!insideBounds(bounds, head.x, head.z)) {
        const [cx, cz] = clampToBounds(bounds, head.x, head.z);
        o.position.x += cx - head.x;
        o.position.z += cz - head.z;
      }
    }
    if (cache.current.length === 0) { vrPath.reset(); return; }

    // Walls: undo any move (thumbstick, teleport, walking in your room) whose
    // chest-height path crosses solid geometry — imported venues are one big
    // hollow mesh, which the box push-out below deliberately ignores, so this
    // is what keeps you inside the room.
    {
      const o = originRef.current;
      o.updateMatrixWorld();
      const chest = o.localToWorld(_chest.copy(vrHead.pos));
      chest.y -= 0.4;
      if (vrSeat.current) vrPath.reset();
      else if (vrPath.prev) {
        const dx = chest.x - vrPath.prev.x, dz = chest.z - vrPath.prev.z;
        const moved = Math.hypot(dx, dz);
        // Small steps keep a body's width from the wall; a teleport may land
        // right beside one, and may hop a railing (only a full wall stops it).
        const meshes = cache.current.map((c) => c.mesh);
        // A hop needs floor to land on: over a balcony rail into thin air is out.
        const footing = () => {
          const feet = o.position.y - vrLift.applied;
          return cache.current.some(({ box: b }) => chest.x > b.min.x && chest.x < b.max.x && chest.z > b.min.z && chest.z < b.max.z && b.max.y <= feet + 0.6 && b.max.y > feet - 3);
        };
        // Furniture (pods, tables) sits under the chest-height test: walking
        // into it is stopped at knee height instead. Teleports pass over it.
        const furniture = moved < 0.5 ? meshes.filter(isFurniture) : [];
        const knee = o.position.y - vrLift.applied + KNEE;
        const blocked = moved < 0.5
          ? pathBlocked(meshes, vrPath.prev, chest, 0.25)
            || (furniture.length > 0 && pathBlocked(furniture, _kneeA.set(vrPath.prev.x, knee, vrPath.prev.z), _kneeB.set(chest.x, knee, chest.z), 0.25))
          : pathBlocked(meshes, vrPath.prev, chest) && (jumpBlocked(meshes, vrPath.prev, chest) || !footing());
        if (moved > 0.005 && blocked) {
          o.position.x -= dx;
          o.position.z -= dz;
          o.position.y = prevOriginY.current;
          chest.copy(vrPath.prev);
        }
      }
      vrPath.prev = (vrPath.prev ?? new THREE.Vector3()).copy(chest);
      prevOriginY.current = o.position.y;
    }

    const r = 0.4;
    const px = originRef.current.position.x;
    const pz = originRef.current.position.z;

    // Seated: the seat owns the rig's height and position. Moving away (stick
    // locomotion, teleport) stands the player up and hands back to collision.
    if (vrSeat.current) {
      if (Math.hypot(px - vrSeat.current.x, pz - vrSeat.current.z) < 0.25) return;
      standVR(useWorldStore.getState().actualWorldId || '');
    }

    // Vertical support. The rig had none: it kept whatever height the spawn
    // gave it, so in a venue whose floors sit below the world origin you stand
    // in the air (or, from inside the tower, at its base) and stairs do
    // nothing. Standing on the highest surface under the rig — within a step
    // of the current height — gives VR the same footing the desktop avatar
    // has, which is also what makes stairs climbable in a headset.
    const STEP = 0.6;
    // Seated mode lifts the rig above the floor; feet are below that.
    const lift = vrLift.applied;
    const feet = originRef.current.position.y - lift;
    let support = -Infinity;
    for (const entry of cache.current) {
      const b = entry.box;
      if (px < b.min.x - 0.05 || px > b.max.x + 0.05) continue;
      if (pz < b.min.z - 0.05 || pz > b.max.z + 0.05) continue;
      if (b.max.y <= feet + STEP && b.max.y > support) support = b.max.y;
    }
    if (support > -Infinity) {
      const dy = support + lift - originRef.current.position.y;
      // Ease up onto ledges, drop a little faster, never teleport.
      originRef.current.position.y += dy > 0 ? Math.min(dy, 0.12) : Math.max(dy, -0.25);
    }

    for (const entry of cache.current) {
      _colBox.copy(entry.box);
      // Same rules as the desktop avatar: anything low enough to step onto
      // (floor tiers, stage steps, cushions) is footing, never a wall. Without
      // this, a rig that starts inside a raised tier was shoved toward the
      // tier's nearest edge — through the room's wall — and stranded outside.
      if (_colBox.max.y <= feet + STEP) continue;
      if (feet + 0.5 < _colBox.min.y || feet + 0.5 > _colBox.max.y) continue;
      const minX = _colBox.min.x - r, maxX = _colBox.max.x + r;
      const minZ = _colBox.min.z - r, maxZ = _colBox.max.z + r;
      if (px > minX && px < maxX && pz > minZ && pz < maxZ) {
        const el = px - minX, er = maxX - px, eb = pz - minZ, ef = maxZ - pz;
        const m = Math.min(el, er, eb, ef);
        // Deep inside a box means a hollow structure (a room, a venue), not
        // solid geometry — pushing out would eject the player through a wall.
        if (m > r + 0.6) continue;
        const f = 0.06;
        if (m === el) originRef.current.position.x -= f;
        else if (m === er) originRef.current.position.x += f;
        else if (m === eb) originRef.current.position.z -= f;
        else originRef.current.position.z += f;
      }
    }
  }, 3, true);
  return null;
};

// ---- VR player rig (viewer mode — no build tools) ----
const ViewerVRRig: React.FC<{ avatarUrl?: string; worldId: string; isOwner?: boolean; home?: boolean }> = ({ avatarUrl, worldId, isOwner, home = false }) => {
  const isPresenting = useXR((s) => !!s.session);
  const originRef = useRef<THREE.Group>(null);
  const spawnPosition = useWorldStore((s) => s.spawnPosition);
  const userMovedRef = useRef(false);

  // Place the XR origin at the world's saved spawn point. The editor's spawn
  // marker is a floor pad, so spawnPosition is FLOOR-level — same level as the
  // XR origin (the headset adds eye height itself). Re-applies if the spawn
  // loads after VR entry, but never after the user teleports away.
  useEffect(() => {
    if (!isPresenting) { userMovedRef.current = false; return; }
    if (userMovedRef.current || !originRef.current) return;
    originRef.current.position.set(spawnPosition[0], spawnPosition[1], spawnPosition[2]);
    vrPath.reset();
  }, [isPresenting, spawnPosition]);

  const { scene } = useThree();
  const handleTeleport = useCallback((point: Vector3) => {
    if (!originRef.current) return;
    // Never land outside the world's walkable bounds.
    if (!insideBounds(useWorldStore.getState().worldBounds, point.x, point.z)) return;
    userMovedRef.current = true;
    // Teleporting onto a seat sits you in it.
    const seat = findSeat(scene, point.x, point.z, 0.8);
    if (seat) { sitVR(originRef.current, seat, worldId); return; }
    if (onFurniture(furnitureBoxes(scene), point.x, point.z)) return; // not onto a table or pod
    if (vrSeat.current) standVR(worldId);
    originRef.current.position.x = point.x;
    originRef.current.position.z = point.z;
  }, [scene, worldId]);
  // Hand teleport lands on the surface it aimed at (stairs, stage tiers,
  // upper decks) — the arc already found its height.
  // Hands sit only when aiming right at a seat (the arc ring turns gold);
  // landing on the floor beside one just moves you there.
  const seatRadius = (flat: boolean) => (flat ? 0.4 : 0.75); // on top of it, or against its side
  const handleHandTeleport = useCallback((point: Vector3, sit?: boolean) => {
    if (!originRef.current) return;
    if (!insideBounds(useWorldStore.getState().worldBounds, point.x, point.z)) return;
    userMovedRef.current = true;
    const seat = sit ? findSeat(scene, point.x, point.z, 0.75, point.y) : null;
    if (seat) { sitVR(originRef.current, seat, worldId); return; }
    if (vrSeat.current) standVR(worldId);
    originRef.current.position.set(point.x, point.y + vrLift.applied, point.z);
    // The arc already refused landings past a wall; don't second-guess it with
    // the collision boxes (a landing beside a pillar would silently bounce back).
    vrPath.reset();
  }, [scene, worldId]);
  // Portal: put the headset over the arrival point, facing out of the portal.
  usePortalArrival(({ position, yaw }) => {
    const o = originRef.current;
    if (!isPresenting || !o) return;
    userMovedRef.current = true;
    if (vrSeat.current) standVR(worldId);
    const look = new THREE.Vector3(0, 0, -1).applyQuaternion(vrHead.quat);
    o.rotation.y = yaw + Math.PI - Math.atan2(-look.x, -look.z);
    o.updateMatrixWorld(true);
    const head = o.localToWorld(vrHead.pos.clone());
    o.position.x += position[0] - head.x;
    o.position.z += position[2] - head.z;
    o.position.y = position[1] + vrLift.applied;
    vrPath.reset();
  });
  // Game-table chair: keep the eyes TABLE_EYE above the tabletop whatever
  // your real posture. Measured from the headset's rig-local height against
  // the tabletop's height above the seat's root, once on sitting and again
  // only after a sustained change (seatedMode.createTableLiftTracker).
  const tableLift = useMemo(() => createTableLiftTracker(), []);
  const liftSeat = useRef<typeof vrSeat.current>(null);
  // Seated mode / table lift: raise or lower the rig by the change in lift,
  // eased so it never snaps in the headset.
  useFrame((_, dt) => {
    const o = originRef.current;
    if (!isPresenting || !o) return;
    const seat = vrSeat.current;
    if (seat !== liftSeat.current) { liftSeat.current = seat; tableLift.reset(); }
    vrLift.table = seat?.seat.tableTop != null
      ? tableLift.update(performance.now(), seat.seat.tableTop - (o.position.y - vrLift.applied), vrHead.pos.y)
      : 0;
    const want = wantedLift(!!seat);
    if (want !== vrLift.applied) {
      const next = easeLift(vrLift.applied, want, dt);
      o.position.y += next - vrLift.applied;
      vrLift.applied = next;
    }
  });

  if (!isPresenting) return null;

  return (
    <>
      <XROrigin ref={originRef}>
        {avatarUrl
          ? <AvatarSwitchGuard key={avatarUrl} url={avatarUrl}><VRAvatarBody avatarUrl={avatarUrl} /></AvatarSwitchGuard>
          : <VRAvatarBody />}
        <VRHandControls originRef={originRef} onTeleport={handleHandTeleport} seatNear={(p, flat) => !!findSeat(scene, p.x, p.z, seatRadius(flat), p.y)}
          onGesture={(emote) => {
            // Seated in a chair, the sit pose stays; gestures play standing.
            if (vrSeat.current) return;
            useWorldStore.getState().playLocalEmote(emote);
            if (worldId) broadcastEmote(worldId, emote);
          }} />
        {/* Menu lives inside the rig so it travels with every form of locomotion */}
        <VRViewerMenu worldId={worldId} isOwner={isOwner} home={home} />
        {vrDebugEnabled() && <VRDebugReadout originRef={originRef} />}
      </XROrigin>

      <TeleportTarget onTeleport={handleTeleport}>
        <mesh name="vr-teleport-floor" rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.49, 0]}>
          <planeGeometry args={[200, 200]} />
          <meshBasicMaterial visible={false} />
        </mesh>
      </TeleportTarget>

      <VRLocomotion
        target={originRef as any}
        moveSpeed={3.0}
        enableTeleport={true}
        enableSmoothLocomotion={true}
        debugThumbstick={false}
      />
      <VRCollision originRef={originRef} />
      <VRBuildModeOptimizer />
    </>
  );
};

// ---- Simplified ground (viewer-only, no texture picker) ----
const ViewerGround: React.FC<{
  visible: boolean;
  size: number;
  color: string;
  y: number;
  opacity?: number;
}> = ({ visible, size, color, y, opacity = 1 }) => {
  if (!visible) return null;
  return (
    <mesh name="ground" receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, y, 0]}>
      <planeGeometry args={[size, size]} />
      <meshStandardMaterial color={color} transparent={opacity < 1} opacity={opacity} depthWrite={opacity >= 1} />
    </mesh>
  );
};

/** A shape's picture (uploaded texture, material-library colour map or a
 *  built-in preset's colour map). Colour only: normal/roughness maps stay
 *  editor-side to keep the viewer light on Quest. */
function primitiveTextureUrl(p: PrimitiveItem): string | null {
  if (p.textureUrl) return p.textureUrl;
  if (p.pbrMaps?.colorUrl) return p.pbrMaps.colorUrl;
  if (p.texturePreset) return `/textures/${p.texturePreset}/color.jpg`;
  return null;
}

const ViewerTexturedMaterial: React.FC<{ url: string; primitive: PrimitiveItem }> = ({ url, primitive }) => {
  const tex = useTexture(url);
  const sx = primitive.textureScale ?? 1, sy = primitive.textureScaleY ?? sx;
  useEffect(() => {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(sx, sy);
    tex.needsUpdate = true;
  }, [tex, sx, sy]);
  return (
    <meshStandardMaterial
      map={tex}
      color={primitive.textureUrl ? '#ffffff' : primitive.color || '#ffffff'}
      transparent={(primitive.opacity ?? 1) < 1}
      opacity={primitive.opacity ?? 1}
      metalness={primitive.metalness ?? 0}
      roughness={primitive.roughness ?? 0.8}
    />
  );
};

// ---- Simplified primitive renderer (viewer-only, no edit) ----
// Tags collidable meshes with userData.collidable so avatar/VR locomotion blocks
// against them (boundary walls). colliderOnly primitives are invisible boundary
// volumes — rendered with a transparent material so their AABB still resolves for
// collision, but nothing shows.
const ViewerPrimitive: React.FC<{ primitive: PrimitiveItem }> = ({ primitive }) => {
  if (!primitive.visible) return null;
  const { shape, position, rotation, scale, color, opacity, metalness, roughness, text, fontSize, collidable, colliderOnly } = primitive;

  // Collider volumes and fully transparent markers (seats) are never drawn —
  // a hidden material skips the draw call (and shadow pass) while raycasts,
  // seat lookups and collision boxes still see the mesh.
  const texUrl = primitiveTextureUrl(primitive);
  const plain = (
    <meshStandardMaterial
      color={color || '#888888'}
      transparent={opacity < 1}
      opacity={opacity ?? 1}
      metalness={metalness ?? 0}
      roughness={roughness ?? 0.5}
    />
  );
  const mat = colliderOnly || (opacity ?? 1) <= 0 ? (
    <meshBasicMaterial visible={false} />
  ) : texUrl && !(primitive.emissive ?? 0) ? (
    <FallbackOnError fallback={plain}><Suspense fallback={plain}><ViewerTexturedMaterial url={texUrl} primitive={primitive} /></Suspense></FallbackOnError>
  ) : (
    <meshStandardMaterial
      color={color || '#888888'}
      transparent={opacity < 1}
      opacity={opacity ?? 1}
      metalness={metalness ?? 0}
      roughness={roughness ?? 0.5}
      emissive={color || '#888888'}
      emissiveIntensity={primitive.emissive ?? 0}
    />
  );

  const s = Array.isArray(scale) ? scale : [scale, scale, scale];

  if (shape === 'text' && text) {
    return (
      <Text
        position={position}
        rotation={rotation}
        userData={{ collidable: !!collidable }}
        fontSize={fontSize || 0.5}
        color={color || '#ffffff'}
        anchorX="center"
        anchorY="middle"
      >
        {text}
      </Text>
    );
  }

  return (
    <mesh position={position} rotation={rotation} scale={s as [number, number, number]} castShadow={primitive.castShadow !== false} receiveShadow userData={{ collidable: !!collidable, ...(primitive.seat ? { seat: true, ...(primitive.seatFit ? { seatFit: primitive.seatFit } : {}) } : {}) }}>
      {shape === 'sphere' && <sphereGeometry args={[0.5, 32, 32]} />}
      {shape === 'cylinder' && <cylinderGeometry args={[0.5, 0.5, 1, 32]} />}
      {shape === 'cone' && <coneGeometry args={[0.5, 1, 32]} />}
      {shape === 'torus' && <torusGeometry args={[0.5, 0.2, 16, 64]} />}
      {shape === 'plane' && <planeGeometry args={[1, 1]} />}
      {shape === 'ring' && <ringGeometry args={[0.3, 0.5, 32]} />}
      {shape === 'capsule' && <capsuleGeometry args={[0.3, 0.5, 8, 16]} />}
      {(!shape || shape === 'box') && <boxGeometry args={[1, 1, 1]} />}
      {mat}
    </mesh>
  );
};

// ---- Simplified light renderer (viewer-only, no edit) ----
const ViewerLight: React.FC<{ light: LightItem }> = ({ light }) => {
  const spotRef = useRef<THREE.SpotLight>(null);
  const targetRef = useRef<THREE.Object3D>(null);

  useEffect(() => {
    if (spotRef.current && targetRef.current) {
      spotRef.current.target = targetRef.current;
    }
  });

  if (!light.visible) return null;

  const targetPos: [number, number, number] = [
    light.position[0],
    light.position[1] - light.distance * 0.5,
    light.position[2],
  ];

  if (light.type === 'pointlight') {
    return (
      <pointLight
        position={light.position}
        color={light.color}
        intensity={light.intensity}
        distance={light.distance}
      />
    );
  }

  if (light.type === 'arealight') {
    return (
      <group position={light.position} rotation={light.rotation}>
        <rectAreaLight
          color={light.color}
          intensity={light.intensity}
          width={light.width ?? 4}
          height={light.height ?? 4}
        />
      </group>
    );
  }

  // spotlight (default)
  return (
    <group position={light.position} rotation={light.rotation}>
      <spotLight
        ref={spotRef}
        color={light.color}
        intensity={light.intensity}
        distance={light.distance}
        angle={light.angle}
        penumbra={light.penumbra}
        castShadow={false}
      />
      <object3D ref={targetRef} position={targetPos} />
    </group>
  );
};

// ---- Personal VR controls, anchored inside the player rig ----
/** Room screens nearest-first from a world-space point (VR share panel targets). */
function roomScreenTargets(from: THREE.Vector3 | null): ShareTarget[] {
  const props = useWorldStore.getState().screenProps;
  const list = Object.entries(props).filter(([, p]) => p.roomScreen && p.visible).map(([id, item]) => ({ id, item }));
  if (from) list.sort((a, b) => from.distanceToSquared(new THREE.Vector3(...a.item.position)) - from.distanceToSquared(new THREE.Vector3(...b.item.position)));
  return list;
}

const VRViewerMenu: React.FC<{ worldId: string; isOwner?: boolean; home?: boolean }> = ({ worldId, isOwner, home = false }) => {
  const isPresenting = useXR((s) => !!s.session);
  const { gl, scene } = useThree();
  const navigate = useNavigate();
  const groupRef = useRef<THREE.Group>(null);
  const headPos = useRef(new THREE.Vector3());
  const headQuat = useRef(new THREE.Quaternion());
  const [seated, setSeated] = useState(false);
  const [sitStyleLabel, setSitStyleLabel] = useState(() => getSitStyle().label);
  const [notice, setNotice] = useState<string | null>(null);

  // Voice state from the shared store. (This menu only mounts once the XR
  // session starts, so window events fired earlier — e.g. joining voice on
  // the 2D page — never reached it and it read "Voice offline".)
  const voicePhase = useVoiceStore((s) => s.phase);
  const isMuted = useVoiceStore((s) => s.muted);
  const micBlocked = useVoiceStore((s) => s.micBlocked);
  // The session just started: retry remote playback so others are heard.
  useEffect(() => { unlockVoiceAudio(); }, []);

  // Chat — mirror the world channel's chat:message broadcasts
  const [chatOpen, setChatOpen] = useState(false);
  // Music row: only when the world has music / speakers or you're a host.
  const showMusic = useShowMusicButton();
  const [musicOpen, setMusicOpen] = useState(false);
  const [messages, setMessages] = useState<{ user: string; text: string }[]>([]);
  useEffect(() => {
    if (!worldId) return;
    let live = true;
    const channel = getChannel(worldId);
    channel.on('broadcast', { event: 'chat:message' }, (payload: any) => {
      const m = payload?.payload;
      if (live && m?.text && !isMutedUser(m.userId, m.key)) {
        setMessages((prev) => [...prev.slice(-5), { user: m.user || 'Player', text: String(m.text).slice(0, 90) }]);
      }
    });
    return () => { live = false; };
  }, [worldId]);

  const [flash, setFlash] = useState(false);
  // At Home the dashboard is the menu: start hidden (A / X or the wrist opens it).
  const [menuVisible, setMenuVisible] = useState(!home);
  // Tell in-world HUDs (e.g. Blackthorn House) where the wrist menu is, so they don't sit on top of it.
  useEffect(() => {
    (window as any).__gxVrMenuOpen = menuVisible;
    window.dispatchEvent(new CustomEvent('gathrxr:vr-menu-state', { detail: { open: menuVisible } }));
    return () => { (window as any).__gxVrMenuOpen = false; };
  }, [menuVisible]);
  const sharePanelOpen = useRoomScreenStore((s) => s.sharePanelOpen);
  const hasRoomScreens = useWorldStore((s) => Object.values(s.screenProps).some((p) => p.roomScreen && p.visible));
  const [shareTargets, setShareTargets] = useState<ShareTarget[]>([]);

  const buttonHeld = useRef(false);
  const menuAnchor = useRef(new VRMenuAnchor());
  const recenterMenu = useRef(true);
  // Hands: the wrist button toggles the menu (controllers use A/X).
  useEffect(() => {
    const onMenu = () => { recenterMenu.current = true; setMenuVisible((v) => !v); };
    window.addEventListener(VR_MENU_EVENT, onMenu);
    return () => window.removeEventListener(VR_MENU_EVENT, onMenu);
  }, []);
  // Starting an arcade game hides the menu: it floats between you and the game
  // screen (eating the ray aimed at its buttons), and A / X can't close it
  // while the game owns the controllers.
  useEffect(() => useArcade.subscribe((st, prev) => { if (st.playing && !prev.playing) setMenuVisible(false); }), []);
  useFrame((state, delta, frame) => {
    if (!groupRef.current || !isPresenting) return;
    // Read the raw reference-space pose, not matrixWorld (which includes the rig).
    const referenceSpace = state.gl.xr.getReferenceSpace();
    const pose = frame && referenceSpace ? frame.getViewerPose(referenceSpace) : null;
    if (!pose) return;
    headPos.current.copy(pose.transform.position);
    headQuat.current.copy(pose.transform.orientation);
    vrHead.pos.copy(headPos.current);
    vrHead.quat.copy(headQuat.current);
    if (seated !== !!vrSeat.current) setSeated(!!vrSeat.current);

    // Joystick summon: A/X button (index 4) toggles the menu
    const session = state.gl.xr.getSession();
    if (session) {
      let pressed = false;
      for (const src of session.inputSources) {
        const btn = src.gamepad?.buttons?.[4];
        if (btn?.pressed) { pressed = true; break; }
      }
      // While an arcade game owns the controllers, A / X is its button, not the menu.
      if (pressed && !buttonHeld.current && !locomotionLocked()) { recenterMenu.current = true; setMenuVisible((v) => !v); }
      buttonHeld.current = pressed;
    }

    // Hands poke the buttons, so the menu comes within reach; controllers
    // point from further back. Follows the input you switch to mid-session.
    const hands = !!session && [...session.inputSources].some((src) => src.hand);
    menuAnchor.current.update(groupRef.current, headPos.current, headQuat.current, delta, recenterMenu.current, hands ? MENU_DIST_HANDS : MENU_DIST_CONTROLLERS);
    recenterMenu.current = false;
  });

  const [emotesOpen, setEmotesOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [seatedMode, setSeatedModeState] = useState(vrLift.on);

  // World hopping: an invite pops the menu up in front of you with the prompt.
  const hop = useVRWorldHop();
  useEffect(() => { if (hop.invite) { recenterMenu.current = true; setMenuVisible(true); } }, [hop.invite?.key]);
  const avatars = useVRAvatarSwitch();

  if (!isPresenting) return null;

  const download = (dataUrl: string, name: string) => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = name;
    a.click();
  };

  // Joins voice if not in the room yet, otherwise toggles mute.
  const handleMute = () => {
    if (!pressVoiceButton()) {
      setNotice('Sign in to use voice chat');
      setTimeout(() => setNotice(null), 2600);
    }
  };
  // Shared offscreen capture — the XR compositor owns the canvas on Quest,
  // so canvas.toDataURL returns black; render + read back pixels instead.
  const captureFrom = (cam: THREE.PerspectiveCamera, name: string, showHead = false) => {
    const W = 1280, H = 960;
    const rt = new THREE.WebGLRenderTarget(W, H);
    const wasXr = gl.xr.enabled;
    // Hide the menu so it doesn't photobomb; un-hide the avatar head for selfies
    const menuWasVisible = groupRef.current?.visible ?? true;
    if (groupRef.current) groupRef.current.visible = false;
    // …and the wrist dial / gesture label (the hand is reaching for the dial).
    const overlays: THREE.Object3D[] = [];
    scene.traverse((o) => { if ((o.name === 'vr-wrist-dial' || o.name === 'vr-gesture-label') && o.visible) overlays.push(o); });
    overlays.forEach((o) => { o.visible = false; });
    const headBone = (window as any).__vrHeadBone as THREE.Bone | null;
    if (showHead && headBone) headBone.scale.set(1, 1, 1);
    scene.updateMatrixWorld(true);
    gl.xr.enabled = false;
    gl.setRenderTarget(rt);
    gl.render(scene, cam);
    const pixels = new Uint8Array(W * H * 4);
    gl.readRenderTargetPixels(rt, 0, 0, W, H, pixels);
    gl.setRenderTarget(null);
    gl.xr.enabled = wasXr;
    if (showHead && headBone) headBone.scale.set(0, 0, 0);
    if (groupRef.current) groupRef.current.visible = menuWasVisible;
    overlays.forEach((o) => { o.visible = true; });
    rt.dispose();
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    if (ctx) {
      const img = ctx.createImageData(W, H);
      for (let y = 0; y < H; y++) {
        img.data.set(pixels.subarray((H - 1 - y) * W * 4, (H - y) * W * 4), y * W * 4);
      }
      ctx.putImageData(img, 0, 0);
      download(c.toDataURL('image/png'), name);
    }
    setFlash(true);
    setTimeout(() => setFlash(false), 180);
  };
  // World-space head pose (head refs are origin-local; lift through the rig)
  const worldHeadPose = () => {
    const origin = groupRef.current?.parent;
    const pos = headPos.current.clone();
    const quat = new THREE.Quaternion();
    if (origin) {
      origin.updateWorldMatrix(true, false);
      origin.localToWorld(pos);
      origin.getWorldQuaternion(quat).multiply(headQuat.current);
    } else {
      quat.copy(headQuat.current);
    }
    return { pos, quat };
  };
  const handlePhoto = () => {
    const { pos, quat } = worldHeadPose();
    const cam = new THREE.PerspectiveCamera(75, 1280 / 960, 0.05, 400);
    cam.position.copy(pos);
    cam.quaternion.copy(quat);
    cam.updateMatrixWorld(true);
    captureFrom(cam, `gathrxr-photo-${Date.now()}.png`);
  };
  // Selfie: render one flat frame from a camera 1.5m in front facing the
  // player (avatar body visible), capture, then hand rendering back to XR.
  const handleSelfie = () => {
    const { pos, quat } = worldHeadPose();
    const cam = new THREE.PerspectiveCamera(55, 1280 / 960, 0.05, 200);
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(quat);
    fwd.y = 0; fwd.normalize();
    cam.position.copy(pos).addScaledVector(fwd, 1.5);
    cam.position.y = pos.y;
    cam.lookAt(pos.x, pos.y - 0.35, pos.z);
    cam.updateMatrixWorld(true);
    captureFrom(cam, `gathrxr-selfie-${Date.now()}.png`, true);
  };
  const questApp = isQuestImmersiveApp();
  // The immersive Quest app has no 2D dashboard: its dashboard is Home. At
  // Home, the same button is the account panel (sign in / out, close app).
  const goHome = () => { setMenuVisible(false); void travelToWorld(VR_HOME_WORLD_ID, VR_HOME_NAME); };
  const handleExit = () => {
    if (questApp) { if (home) openQuestAccountPanel(); else goHome(); return; }
    xrStore.getState().session?.end();
    navigate('/dashboard');
  };

  const handleSit = () => {
    const origin = groupRef.current?.parent;
    if (!origin) return;
    if (vrSeat.current) { standVR(worldId); setSeated(false); return; }
    const head = origin.localToWorld(headPos.current.clone());
    // Feet level (seated mode lifts the rig above the floor).
    const seat = findSeat(scene, head.x, head.z, 3, origin.position.y - vrLift.applied);
    if (!seat) { setNotice('No seat nearby — walk closer to one'); setTimeout(() => setNotice(null), 2200); return; }
    sitVR(origin, seat, worldId);
    setSeated(true);
  };
  // Game tables: taking a seat at a table moves the rig into that chair;
  // leaving the game stands you up.
  useEffect(() => {
    if (!isPresenting) return;
    const onSit = (e: Event) => {
      const marker = (e as CustomEvent).detail?.seat as THREE.Object3D | undefined;
      const origin = groupRef.current?.parent;
      if (!marker || !origin) return;
      const p = marker.getWorldPosition(new THREE.Vector3());
      const seat = findSeat(scene, p.x, p.z, 0.3, p.y);
      if (!seat) return;
      sitVR(origin, seat, worldId);
      setSeated(true);
      // The game's own panel sits in front of you at the table; the menu
      // would cover it (the wrist dial or A / X brings it back).
      setMenuVisible(false);
    };
    const onStand = () => { if (vrSeat.current) { standVR(worldId); setSeated(false); } };
    window.addEventListener('gathrxr:game-sit', onSit);
    window.addEventListener('gathrxr:game-stand', onStand);
    return () => {
      window.removeEventListener('gathrxr:game-sit', onSit);
      window.removeEventListener('gathrxr:game-stand', onStand);
    };
  }, [isPresenting, scene, worldId]);

  const handleSitStyle = () => {
    const i = SIT_STYLES.findIndex((st) => st.emote === getSitStyle().emote);
    const next = SIT_STYLES[(i + 1) % SIT_STYLES.length];
    setSitStyle(next.emote);
    setSitStyleLabel(next.label);
    const origin = groupRef.current?.parent;
    if (origin && vrSeat.current) sitVR(origin, vrSeat.current.seat, worldId);
  };

  const handleSeatedMode = () => {
    setSeatedMode(!vrLift.on, headPos.current.y);
    setSeatedModeState(vrLift.on);
    setNotice(vrLift.on ? 'Seated mode on — your view is raised to standing height' : 'Seated mode off');
    setTimeout(() => setNotice(null), 2600);
  };
  const handleBackToStart = () => {
    const origin = groupRef.current?.parent;
    if (!origin) return;
    if (vrSeat.current) { standVR(worldId); setSeated(false); }
    const sp = useWorldStore.getState().spawnPosition;
    origin.position.set(sp[0], sp[1] + vrLift.applied, sp[2]);
    vrPath.reset();
    setNotice('Back at the start');
    setTimeout(() => setNotice(null), 2200);
  };
  const handleShare = () => {
    const origin = groupRef.current?.parent;
    const head = origin ? origin.localToWorld(headPos.current.clone()) : null;
    setShareTargets(roomScreenTargets(head));
    openSharePanel(true);
  };


  const voiceLive = voicePhase === 'connected' && !isMuted;
  const voiceBtn = voiceButtonLabel({ phase: voicePhase, muted: isMuted, micBlocked });
  // Hidden is not enough: pointer rays still hit invisible meshes, and the
  // hidden menu would swallow every ray aimed past it. 'auto' when shown, not
  // undefined: R3F leaves a removed prop at its last value, so the menu stayed
  // 'none' after the first close and every ray went through it.
  return <group ref={groupRef} visible={menuVisible} pointerEvents={menuVisible ? 'auto' : 'none'}>
    {hop.invite
      ? <group position={[0, .06, 0]}><VRInvitePrompt key={hop.invite.key} invite={hop.invite} /></group>
      : hop.open
      ? <group position={[0, .06, 0]}><VRWorldHopPanel worldId={worldId} onClose={hop.close} /></group>
      : avatars.open
      ? <group position={[0, .06, 0]}><VRAvatarSwitchPanel onClose={avatars.close} /></group>
      : emotesOpen
      ? <group position={[0, .06, 0]}><VREmotePanel onClose={() => setEmotesOpen(false)} /></group>
      : helpOpen
      ? <group position={[0, .06, 0]}><VRHelpPanel onClose={() => setHelpOpen(false)} paused={!menuVisible} /></group>
      : sharePanelOpen && shareTargets.length > 0
      ? <group position={[0, .06, 0]}><VRSharePanel targets={shareTargets} isOwner={isOwner} onClose={() => openSharePanel(false)} /></group>
      : <VRUserMenuSurface voiceLabel={voiceBtn.label} voiceHint={voiceBtn.hint} voiceLive={voiceLive}
        chatOpen={chatOpen} messages={messages} flash={flash}
        onMute={handleMute} onChat={() => { setChatOpen(v => !v); setMusicOpen(false); }} onPhoto={handlePhoto}
        onSelfie={handleSelfie} onCalibrate={requestAvatarCalibration} onExit={handleExit} onHide={() => setMenuVisible(false)}
        notice={notice || hop.toast || avatars.toast} seated={seated} sitStyleLabel={sitStyleLabel} onSit={handleSit} onSitStyle={handleSitStyle}
        onWorlds={hop.openPicker} onAvatars={avatars.openPicker}
        onHome={questApp && !home ? goHome : undefined}
        exitLabel={questApp && home ? 'Account' : undefined} exitHint={questApp ? (home ? 'Sign in, sign out, close app' : 'Back to Home') : undefined}
        onMusic={showMusic ? () => { setMusicOpen((v) => !v); setChatOpen(false); } : undefined} musicOpen={musicOpen}
        musicPanel={musicOpen ? <Suspense fallback={null}><VRMusicPanel worldId={worldId || null} /></Suspense> : null}
        onEmotes={() => setEmotesOpen(true)} onShare={hasRoomScreens ? handleShare : undefined} onHelp={() => { setHelpOpen(true); setChatOpen(false); setMusicOpen(false); }}
        seatedMode={seatedMode} onSeatedMode={handleSeatedMode} onBackToStart={handleBackToStart} />}
    <VRPeopleTab />
  </group>;
};


// ---- Desktop camera pill (Normal / Zoom / First-person) ----
// Centred at the bottom, but never under the tools dock: it publishes its own
// half-width (--gx-campill-half) and the CSS keeps its left edge past
// --gx-dock-right. Below 1100px it collapses to icons + tooltips.
const CAM_PILL_MODES: [DesktopCamMode, string, React.ReactNode][] = [
  ['normal', 'Normal', <PersonStanding key="n" size={16} />],
  ['zoom', 'Zoom', <ZoomIn key="z" size={16} />],
  ['firstPerson', 'First-person', <Eye key="f" size={16} />],
];
const CamPill: React.FC<{ mode: DesktopCamMode; onMode: (m: DesktopCamMode) => void }> = ({ mode, onMode }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const publish = () => el.style.setProperty('--gx-campill-half', `${Math.ceil(el.offsetWidth / 2)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="gx-campill" role="radiogroup" aria-label="Camera">
      {CAM_PILL_MODES.map(([m, label, icon]) => (
        <button
          key={m}
          role="radio"
          aria-checked={mode === m}
          aria-label={label}
          data-tip={label}
          className={`gx-campill__btn${mode === m ? ' is-sel' : ''}`}
          onClick={() => onMode(m)}
        >
          {icon}<span className="gx-campill__label">{label}</span>
        </button>
      ))}
    </div>
  );
};

// ---- Desktop HUD: tutorial overlay + screenshot/recorder (viewer only) ----
// On phones / narrow windows (`bar` set) the capture tools move into the
// compact bottom bar's More sheet, alongside Back, Chat, Emote and camera mode.
type CompactBarProps = Omit<React.ComponentProps<typeof ViewerHudBar>, 'tools' | 'moreBadge'>;
const DesktopViewerHUD: React.FC<{ worldId?: string; isOwner?: boolean; bar?: CompactBarProps }> = ({ worldId, isOwner, bar }) => {
  const [showTutorial, setShowTutorial] = useState(() => {
    try { return !isQuestBrowser && !localStorage.getItem('gathrxr.viewerTutorialSeen'); } catch { return false; }
  });
  const [recording, setRecording] = useState(false);
  const [thumbState, setThumbState] = useState<'idle' | 'busy' | 'done' | 'fail'>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  // The dock publishes its right edge (--gx-dock-right) so the centred camera
  // pill can slide right instead of sitting on top of it on narrow windows.
  const dockRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = dockRef.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--gx-dock-right', `${Math.ceil(el.getBoundingClientRect().right)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    return () => { ro.disconnect(); window.removeEventListener('resize', publish); root.style.removeProperty('--gx-dock-right'); };
  }, [!!bar]);
  // Duplicate-session detection: who else in the room is this same account?
  const onlinePlayers = useWorldStore((s) => s.onlinePlayers);
  const myUserId = useWorldStore((s) => s.user?.id || null);
  const myKey = getPresenceKey();
  // Guestbook — signed-in visitors leave the creator a note; the creator reads
  // and replies here too. Badge shows the owner's unread count.
  const [showGuestbook, setShowGuestbook] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [gbBadge, setGbBadge] = useState(0);
  // Owner: make a shareable /m/:slug meetup link for this world.
  const [meetupFor, setMeetupFor] = useState<{ id: string; name: string } | null>(null);
  const openMeetup = useCallback(async () => {
    if (!worldId) return;
    const fallback = new URLSearchParams(window.location.search).get('name') || 'This world';
    const { data } = await supabase.from('worlds').select('name').eq('id', worldId).maybeSingle();
    setMeetupFor({ id: worldId, name: data?.name || fallback });
  }, [worldId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelled) return;
        setSignedIn(!!user);
      } catch { /* not signed in */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Owners get an at-a-glance unread count without opening the panel.
  useEffect(() => {
    if (!isOwner || !worldId) return;
    let cancelled = false;
    guestbookUnreadCount(worldId)
      .then((n) => { if (!cancelled) setGbBadge(n); })
      .catch(() => { /* guestbook is optional */ });
    return () => { cancelled = true; };
  }, [isOwner, worldId]);

  const getCanvas = () => document.querySelector('canvas') as HTMLCanvasElement | null;
  const musicTool = useMusicHudTool();

  // Owner-only: capture the current view as this world's card thumbnail.
  const captureThumbnail = useCallback(async () => {
    const c = getCanvas();
    if (!c || !worldId) return;
    setThumbState('busy');
    const url = await captureCanvasAsWorldThumbnail(c, worldId);
    setThumbState(url ? 'done' : 'fail');
    setTimeout(() => setThumbState('idle'), 2500);
  }, [worldId]);

  // Desktop app (GathrXR Studio) menu hook: ⇧⌘T works in the viewer too.
  useEffect(() => {
    if (!isOwner || !worldId) return;
    (window as any).__gathrxrCaptureThumbnail = captureThumbnail;
    return () => { if ((window as any).__gathrxrCaptureThumbnail === captureThumbnail) delete (window as any).__gathrxrCaptureThumbnail; };
  }, [isOwner, worldId, captureThumbnail]);

  const screenshot = () => {
    const c = getCanvas();
    if (!c) return;
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `gathrxr-${Date.now()}.png`;
    a.click();
  };

  const toggleRecord = () => {
    if (recording) {
      recorderRef.current?.stop();
      setRecording(false);
      return;
    }
    const c = getCanvas();
    if (!c) return;
    try {
      const stream: MediaStream = (c as any).captureStream(30);
      // Game sound effects + world music go into the video too.
      const sound = startRecordingAudio();
      if (sound.track) stream.addTrack(sound.track);
      const mimeType = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9', 'video/webm'].find((t) => MediaRecorder.isTypeSupported(t));
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        sound.stop();
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `gathrxr-recording-${Date.now()}.webm`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      console.error('[HUD] Recording failed:', err);
      alert('Recording is not supported in this browser.');
    }
  };

  if (isQuestBrowser && !bar) return null;

  // Other tabs of THIS account currently in the room. Each tab is its own
  // participant, so opening a world twice leaves a second copy of you standing
  // around; this offers a way to clear them without hunting for the tab.
  const dupes = signedIn
    ? onlinePlayers.filter((p) => p.userId && p.userId === myUserId && p.id !== myKey).length
    : 0;

  const tools: HudTool[] = [
    ...(musicTool ? [musicTool] : []),
    { key: 'people', icon: <PeopleIcon size={17} />, label: 'People', onClick: () => openPeopleList(), title: 'Everyone here: mute, block or report' },
    ...(worldId ? [{ key: 'worlds', icon: <Globe size={17} />, label: 'Worlds', onClick: openWorldHop, title: 'Go to another world, and invite people here along' }] : []),
    { key: 'avatar', icon: <Shirt size={17} />, label: 'Avatar', onClick: openAvatarPicker, title: 'Change into another of your avatars, or get a new one' },
    ...(isOwner && worldId ? [{ key: 'meetup', icon: <CalendarDays size={17} />, label: 'Meetup link', onClick: () => void openMeetup(), title: 'Make a link people can use to meet you here' }] : []),
    ...(isQuestBrowser ? [] : [
    { key: 'shot', icon: <Camera size={17} />, label: 'Screenshot', onClick: screenshot, title: 'Save a screenshot' },
    recording
      ? { key: 'rec', icon: <Square size={15} fill="currentColor" />, label: 'Stop & save', onClick: toggleRecord, tone: 'danger' }
      : { key: 'rec', icon: <Video size={17} />, label: 'Record', onClick: toggleRecord, title: 'Record a video of this view' },
    ...(isOwner && worldId ? [{
      key: 'thumb', keepOpen: true, onClick: captureThumbnail, disabled: thumbState === 'busy',
      title: "Use this view as the world's card thumbnail",
      tone: thumbState === 'done' ? 'accent' as const : thumbState === 'fail' ? 'danger' as const : undefined,
      icon: thumbState === 'busy' ? <Loader size={17} className="spin" /> : thumbState === 'done' ? <Check size={17} /> : thumbState === 'fail' ? <AlertTriangle size={17} /> : <ImageIcon size={17} />,
      label: thumbState === 'busy' ? 'Capturing…' : thumbState === 'done' ? 'Thumbnail set' : thumbState === 'fail' ? 'Failed — retry' : 'Set as thumbnail',
    }] : []),
    ...(signedIn && worldId ? [{
      key: 'gb', icon: <BookOpen size={17} />, label: `Guestbook${gbBadge > 0 ? ` (${gbBadge})` : ''}`,
      onClick: () => setShowGuestbook((v) => !v), tone: showGuestbook || gbBadge > 0 ? 'accent' as const : undefined,
    }] : []),
    ...(dupes > 0 ? [{
      key: 'dupes', icon: <UserX size={17} />, label: `Close my other ${dupes === 1 ? 'tab' : `tabs (${dupes})`}`, tone: 'warn' as const,
      onClick: () => (window as any).__gxEvictOtherSessions?.(), title: 'You have this world open in another tab. Close those sessions.',
    }] : []),
    ] as HudTool[]),
  ];

  return (
    <>
      {bar && <ViewerHudBar {...bar} tools={tools} moreBadge={gbBadge > 0 || dupes > 0} />}
      {/* Capture buttons — icon-only dock; the name shows as a tooltip on
          hover / keyboard focus (CSS, from aria-label). */}
      {!bar && <div className="gx-dock" ref={dockRef} role="toolbar" aria-label="World tools">
        {worldId && (
          <button className="gx-dock__btn is-accent" onClick={openWorldHop} aria-label="Worlds" data-tip="Worlds" data-wh="open">
            <Globe size={18} />
          </button>
        )}
        <button className="gx-dock__btn" onClick={openAvatarPicker} aria-label="Avatar" data-tip="Avatar" data-as="open">
          <Shirt size={18} />
        </button>
        <button className="gx-dock__btn" onClick={() => openPeopleList()} aria-label="People" data-tip="People" data-safety="people">
          <PeopleIcon size={18} />
        </button>
        <MusicDockButton />
        <button className="gx-dock__btn" onClick={screenshot} aria-label="Screenshot" data-tip="Screenshot">
          <Camera size={18} />
        </button>
        <button
          className={`gx-dock__btn${recording ? ' is-danger is-live' : ''}`}
          onClick={toggleRecord}
          aria-label={recording ? 'Stop and save recording' : 'Record video'}
          aria-pressed={recording}
          data-tip={recording ? 'Stop & save recording' : 'Record video'}
        >
          {recording ? <Square size={14} fill="currentColor" /> : <Video size={18} />}
        </button>
        {isOwner && worldId && (
          <button
            className={`gx-dock__btn${thumbState === 'done' ? ' is-accent' : thumbState === 'fail' ? ' is-danger' : ''}`}
            onClick={captureThumbnail}
            disabled={thumbState === 'busy'}
            aria-label="Set as world thumbnail"
            data-tip={thumbState === 'busy' ? 'Capturing…' : thumbState === 'done' ? 'Thumbnail set' : thumbState === 'fail' ? 'Failed — retry' : 'Set as world thumbnail'}
          >
            {thumbState === 'busy'
              ? <Loader size={18} className="spin" />
              : thumbState === 'done'
                ? <Check size={18} />
                : thumbState === 'fail'
                  ? <AlertTriangle size={18} />
                  : <ImageIcon size={18} />}
          </button>
        )}
        {dupes > 0 && (
          <button
            className="gx-dock__btn is-warn"
            onClick={() => (window as any).__gxEvictOtherSessions?.()}
            aria-label={`Close my other ${dupes === 1 ? 'tab' : `${dupes} tabs`}`}
            data-tip={`Close my other ${dupes === 1 ? 'tab' : `tabs (${dupes})`}`}
          >
            <UserX size={18} />
            {dupes > 1 && <span className="gx-dock__badge" aria-hidden="true">{dupes}</span>}
          </button>
        )}
        {isOwner && worldId && (
          <button className="gx-dock__btn" onClick={() => void openMeetup()} aria-label="Meetup link" data-tip="Meetup link">
            <CalendarDays size={18} />
          </button>
        )}
        {signedIn && worldId && (
          <button
            className={`gx-dock__btn${showGuestbook || gbBadge > 0 ? ' is-accent' : ''}${showGuestbook ? ' is-on' : ''}`}
            onClick={() => setShowGuestbook((v) => !v)}
            aria-label={`Guestbook${gbBadge > 0 ? `, ${gbBadge} ${isOwner ? 'unread' : 'notes'}` : ''}`}
            aria-pressed={showGuestbook}
            data-tip={`Guestbook${gbBadge > 0 ? ` (${gbBadge} ${isOwner ? 'unread' : 'notes'})` : ''}`}
          >
            <BookOpen size={18} />
            {gbBadge > 0 && <span className="gx-dock__badge" aria-hidden="true">{gbBadge > 99 ? '99+' : gbBadge}</span>}
          </button>
        )}
      </div>}

      {meetupFor && (
        <React.Suspense fallback={null}>
          <MeetupDialog worlds={[meetupFor]} onClose={() => setMeetupFor(null)} />
        </React.Suspense>
      )}

      {showGuestbook && worldId && (
        <GuestbookPanel
          worldId={worldId}
          isOwner={!!isOwner}
          onClose={() => setShowGuestbook(false)}
          onCountsChange={({ total, unread }) => setGbBadge(isOwner ? unread : total)}
        />
      )}

      {/* First-visit tutorial */}
      {showTutorial && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1500,
          background: 'rgba(4,10,18,0.72)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'rgba(12,28,48,0.95)', border: '1px solid rgba(0,194,179,0.3)',
            borderRadius: 20, padding: '32px 40px', maxWidth: 420, color: '#eaf6f8',
            fontFamily: 'Outfit, sans-serif', boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}>
            <h2 style={{ margin: '0 0 18px', fontSize: 22 }}>Welcome to the world 👋</h2>
            {[
              ['W A S D', 'Move around'],
              ['Mouse', 'Look around (click the world first)'],
              ['Shift', 'Run'],
              ['Click ground', 'Walk to that spot'],
              // Previews (no worldId) have no room to chat in.
              ...(worldId ? [['💬 button', 'Chat with everyone here']] : []),
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 14, marginBottom: 10, alignItems: 'center' }}>
                <span style={{
                  background: 'rgba(0,194,179,0.12)', border: '1px solid rgba(0,194,179,0.3)',
                  borderRadius: 8, padding: '4px 10px', fontSize: 13, minWidth: 96, textAlign: 'center',
                }}>{k}</span>
                <span style={{ fontSize: 14, color: 'rgba(234,246,248,0.75)' }}>{v}</span>
              </div>
            ))}
            <button
              onClick={() => {
                setShowTutorial(false);
                try { localStorage.setItem('gathrxr.viewerTutorialSeen', '1'); } catch { /* private mode */ }
              }}
              style={{
                marginTop: 16, width: '100%', padding: '12px 0', borderRadius: 999, border: 'none',
                background: 'linear-gradient(135deg, #00c2b3, #0077b6)', color: '#fff',
                fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'Outfit, sans-serif',
              }}
            >
              Got it — let me in
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ---- Desktop first-person camera for visitors (no avatar required) ----
/** One model that fails to load (a dead link, a site that blocks loading)
 *  is left out instead of taking the whole scene down with it. */
class SkipOnError extends React.Component<{ children: React.ReactNode; name: string }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error) { console.warn(`[viewer] ${this.props.name} could not load, skipping it:`, error.message); }
  render() { return this.state.failed ? null : this.props.children; }
}

// WASD + mouse look via pointer lock. Starts at world spawn position.
// Raycasts against marble collider meshes to prevent walking through walls.
const _fpFwd = new THREE.Vector3();
const _fpRight = new THREE.Vector3();
const _fpUp = new THREE.Vector3(0, 1, 0);
const _fpRaycaster = new THREE.Raycaster();

// Eye height of the view-only first-person camera. MultiplayerSync and
// PortalTraveler subtract the same 1.6 m to find a camera-driven visitor's feet.
const FP_EYE_HEIGHT = 1.6;

const DesktopFirstPersonControls: React.FC<{
  spawnPosition: [number, number, number];
  ready: boolean;
  onLockChange: (locked: boolean) => void;
  // Receives camera as a Group-compatible ref so MultiplayerSync can broadcast position
  positionRef?: React.MutableRefObject<THREE.Group | null>;
}> = ({ spawnPosition, ready, onLockChange, positionRef }) => {
  const { camera, gl, scene } = useThree();
  const yaw = useRef(0);
  const pitch = useRef(0);
  const isLocked = useRef(false);
  const keysDown = useRef(new Set<string>());
  const spawned = useRef(false);
  // Floor under the visitor; the camera stands FP_EYE_HEIGHT above it.
  const floorY = useRef(spawnPosition[1]);
  const colliderCache = useRef<THREE.Object3D[]>([]);
  const lastCacheMs = useRef(0);

  // Expose camera as position source for MultiplayerSync — once it stands at
  // the spawn point. Before the world loads it sits at the default camera
  // position, and peers would see this visitor there.
  useEffect(() => {
    if (!ready || !positionRef) return;
    positionRef.current = camera as unknown as THREE.Group;
    return () => { positionRef.current = null; };
  }, [camera, positionRef, ready]);

  // Position camera at spawn once world data is ready
  useEffect(() => {
    if (!ready || spawned.current) return;
    spawned.current = true;
    camera.rotation.order = 'YXZ';
    // The spawn marker is a floor pad (see the XR origin above), so stand the
    // eyes at standing height over it — not on the floor.
    floorY.current = spawnPosition[1];
    camera.position.set(spawnPosition[0], spawnPosition[1] + FP_EYE_HEIGHT, spawnPosition[2]);
    camera.rotation.set(0, 0, 0);
    yaw.current = 0;
    pitch.current = 0;
  }, [ready, camera, spawnPosition]);

  // Portal: step out at the arrival point, eyes at standing height, facing out.
  usePortalArrival(({ position, yaw: face }) => {
    floorY.current = position[1];
    camera.position.set(position[0], position[1] + FP_EYE_HEIGHT, position[2]);
    yaw.current = face + Math.PI; // camera looks down -Z
    camera.rotation.y = yaw.current;
  });

  // Pointer lock + mouse look + keyboard
  useEffect(() => {
    const canvas = gl.domElement;

    const onLockChange_ = () => {
      const locked = document.pointerLockElement === canvas;
      isLocked.current = locked;
      onLockChange(locked);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isLocked.current) return;
      yaw.current -= e.movementX * 0.002;
      pitch.current = Math.max(-1.4, Math.min(1.4, pitch.current - e.movementY * 0.002));
      camera.rotation.order = 'YXZ';
      camera.rotation.y = yaw.current;
      camera.rotation.x = pitch.current;
    };
    const onClick = () => { if (!isLocked.current) canvas.requestPointerLock(); };
    const kd = (e: KeyboardEvent) => keysDown.current.add(e.code);
    const ku = (e: KeyboardEvent) => keysDown.current.delete(e.code);

    canvas.addEventListener('click', onClick);
    document.addEventListener('pointerlockchange', onLockChange_);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('keydown', kd);
    document.addEventListener('keyup', ku);
    return () => {
      canvas.removeEventListener('click', onClick);
      document.removeEventListener('pointerlockchange', onLockChange_);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('keydown', kd);
      document.removeEventListener('keyup', ku);
      if (document.pointerLockElement === canvas) document.exitPointerLock();
    };
  }, [camera, gl, onLockChange]);

  useFrame((_, delta) => {
    if (isTouchDevice) {
      // Phones: drag to look (no pointer lock), joystick to move.
      const l = takeTouchLook();
      if (l.dx || l.dy) {
        yaw.current -= l.dx * 0.005;
        pitch.current = Math.max(-1.4, Math.min(1.4, pitch.current - l.dy * 0.005));
        camera.rotation.order = 'YXZ';
        camera.rotation.y = yaw.current;
        camera.rotation.x = pitch.current;
      }
    } else if (!isLocked.current) return;

    const k = keysDown.current;
    let mz = 0, mx = 0;
    if (k.has('KeyW') || k.has('ArrowUp')) mz -= 1;
    if (k.has('KeyS') || k.has('ArrowDown')) mz += 1;
    if (k.has('KeyA') || k.has('ArrowLeft')) mx -= 1;
    if (k.has('KeyD') || k.has('ArrowRight')) mx += 1;
    if (mx === 0 && mz === 0 && touchMoveAmount()) { mx = touchMove.x; mz = -touchMove.z; }
    if (mx === 0 && mz === 0) return;

    // Refresh collider cache every 2s
    const now = performance.now();
    if (now - lastCacheMs.current > 2000) {
      colliderCache.current = [];
      scene.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh &&
            (obj.userData.isMarbleCollider || obj.userData.collidable)) {
          colliderCache.current.push(obj);
        }
      });
      lastCacheMs.current = now;
    }

    // Horizontal move direction from yaw only (no pitch)
    const sinY = Math.sin(yaw.current);
    const cosY = Math.cos(yaw.current);
    // forward = (-sinY, 0, -cosY), right = (cosY, 0, -sinY)
    const dx = cosY * mx + sinY * mz;
    const dz = -sinY * mx + cosY * mz;
    const len = Math.sqrt(dx * dx + dz * dz) || 1;
    _fpFwd.set(dx / len, 0, dz / len);

    // Collision: ray from torso height in movement direction
    const SPEED = 3.5;
    const COLLIDE_DIST = 0.45;
    let blocked = false;
    if (colliderCache.current.length > 0) {
      const origin = camera.position.clone();
      origin.y -= 0.5; // torso height
      _fpRaycaster.set(origin, _fpFwd);
      const hits = _fpRaycaster.intersectObjects(colliderCache.current, false);
      blocked = hits.length > 0 && hits[0].distance < COLLIDE_DIST;
      // Knee height too: tables and seats are below the torso ray.
      if (!blocked) {
        origin.y = floorY.current + KNEE;
        _fpRaycaster.set(origin, _fpFwd);
        // Furniture only: a ramp or step at knee height must stay climbable.
        const low = _fpRaycaster.intersectObjects(colliderCache.current, false).filter((h) => isFurniture(h.object));
        blocked = low.length > 0 && low[0].distance < COLLIDE_DIST;
      }
    }

    if (!blocked) {
      camera.position.x += _fpFwd.x * SPEED * delta;
      camera.position.z += _fpFwd.z * SPEED * delta;
    }
    // Stay inside the world's walkable bounds (settings.bounds).
    const fpBounds = useWorldStore.getState().worldBounds;
    if (fpBounds) [camera.position.x, camera.position.z] = clampToBounds(fpBounds, camera.position.x, camera.position.z);

    // Lock vertical position to eye height over the floor (no flying/falling)
    camera.position.y = floorY.current + FP_EYE_HEIGHT;
  });

  return null;
};

// ---- Desktop player: third-person avatar (if the visitor has one) or the
// first-person tour fallback. Guards against VR the same way WorldBuilder's
// AvatarManager does — FixedRetargetedAvatar has no XR guard of its own, and
// would fight the XROrigin for the camera if it ran in a session. Exactly one
// of the two branches feeds MultiplayerSync via localAvatarRef at a time.
type DesktopCamMode = 'normal' | 'zoom' | 'firstPerson';
const DesktopViewerPlayer: React.FC<{
  avatarUrl: string | null;
  userName: string;
  spawnPosition: [number, number, number];
  ready: boolean;
  cameraMode: DesktopCamMode;
  onLockChange: (locked: boolean) => void;
  localAvatarRef: React.MutableRefObject<THREE.Group | null>;
}> = ({ avatarUrl, userName, spawnPosition, ready, cameraMode, onLockChange, localAvatarRef }) => {
  const isPresenting = useXR((s) => !!s.session);
  // Avatar switching: the model URL this player last showed, and the URL that
  // replaced it mid-session (that one takes over the old body's pose).
  const shownUrl = useRef<string | null>(null);
  const swapTo = useRef<string | null>(null);
  if (isPresenting) { shownUrl.current = null; swapTo.current = null; return null; }

  const hasAvatar = !!avatarUrl && avatarUrl !== 'skip';

  if (hasAvatar) {
    // Wait for the world (and its saved spawn point) to load before mounting —
    // the avatar initializes its position exactly once on mount, so mounting
    // early would spawn it at the default origin instead of the world's spawn.
    if (!ready) return null;
    const url = avatarUrl as string;
    if (shownUrl.current && shownUrl.current !== url) swapTo.current = url;
    shownUrl.current = url;
    const swapping = swapTo.current === url;
    // While a swapped-in model is still downloading (one not preloaded, e.g.
    // straight from the create flow), a ghost stands where you were.
    let ghost: React.ReactNode = null;
    const last = localAvatarRef.current;
    if (swapping && last) {
      const ghostRef = { current: { position: [last.position.x, last.position.y, last.position.z] as [number, number, number], rotation: last.rotation.y } };
      ghost = <GhostAvatar transformRef={ghostRef} name={userName} loading />;
    }
    return (
      <AvatarSwitchGuard key={url} url={url}>
        <Suspense fallback={ghost}>
          <FixedRetargetedAvatar
            avatarUrl={url}
            position={spawnPosition}
            carryPose={swapping}
            userName={userName}
            firstPerson={cameraMode === 'firstPerson'}
            cameraDistanceOverride={cameraMode === 'zoom' ? 3 : undefined}
            onLockChange={onLockChange}
            onGroupRef={(ref) => { localAvatarRef.current = ref; if (import.meta.env.DEV) (window as any).__gxAvatar = ref; }}
          />
        </Suspense>
      </AvatarSwitchGuard>
    );
  }

  // No avatar → first-person tour camera (unchanged behaviour).
  return (
    <DesktopFirstPersonControls
      spawnPosition={spawnPosition}
      ready={ready}
      onLockChange={onLockChange}
      positionRef={localAvatarRef}
    />
  );
};

// ---- Main VR Viewer page ----
const isQuestBrowser = typeof navigator !== 'undefined' &&
  /Quest|OculusBrowser|GathrXR/i.test(navigator.userAgent);

// ---- Non-XR harness: the VR share panel (and menu) pinned in front of the
// desktop camera, driven by the mouse like a controller ray. ----
const VRSharePanelPreview: React.FC<{ isOwner?: boolean }> = ({ isOwner }) => {
  const ref = useRef<THREE.Group>(null);
  const camera = useThree((s) => s.camera);
  const open = useRoomScreenStore((s) => s.sharePanelOpen);
  const screenProps = useWorldStore((s) => s.screenProps);
  const targets = React.useMemo(() => roomScreenTargets(camera.position.clone()), [screenProps, open, camera]);
  const inXR = useXR((s) => !!s.session);
  const [emotes, setEmotes] = useState(false);
  const [help, setHelp] = useState(false);
  const hop = useVRWorldHop();
  const avatars = useVRAvatarSwitch();
  useFrame(() => {
    const g = ref.current;
    if (!g) return;
    g.position.copy(camera.position).add(new THREE.Vector3(0, -0.05, -1.05).applyQuaternion(camera.quaternion));
    g.quaternion.copy(camera.quaternion);
  });
  if (inXR) return null;
  return <group ref={ref}>
    {hop.invite ? <VRInvitePrompt key={hop.invite.key} invite={hop.invite} />
      : hop.open ? <VRWorldHopPanel worldId={currentWorldId() || undefined} onClose={hop.close} />
      : avatars.open ? <VRAvatarSwitchPanel onClose={avatars.close} />
      : help ? <VRHelpPanel onClose={() => setHelp(false)} />
      : emotes ? <VREmotePanel onClose={() => setEmotes(false)} /> : open && targets.length > 0
      ? <VRSharePanel targets={targets} isOwner={isOwner} onClose={() => openSharePanel(false)} />
      : <VRUserMenuSurface voiceLabel="Voice offline" voiceLive={false} chatOpen={false} messages={[]} flash={false}
        seated={false} sitStyleLabel="Chair" onMute={() => {}} onChat={() => {}} onPhoto={() => {}} onSelfie={() => {}}
        onCalibrate={() => {}} onExit={() => {}} onHide={() => {}} onSit={() => {}} onSitStyle={() => {}}
        onEmotes={() => setEmotes(true)} onShare={targets.length ? () => openSharePanel(true) : undefined} onWorlds={hop.openPicker} onAvatars={avatars.openPicker} onHelp={() => setHelp(true)} notice={avatars.toast} />}
  </group>;
};

const VRViewer: React.FC<{ localBlackthorn?: boolean; localLastBite?: boolean; localCarnival?: boolean }> = ({ localBlackthorn = false, localLastBite = false, localCarnival = false }) => {
  const seatedAtGame = useSeatedAtGame();
  // /vr/:worldId — a real world. /marketplace/preview/:listingId — "Try it":
  // the world a listing sells, read-only and sealed off from the seller's live
  // room. In preview `worldId` stays undefined on purpose: every multiplayer,
  // voice, chat, room-screen, guestbook and save path keys off it, so they all
  // stay off. Features that only need a private room key (game tables, arcade)
  // get a per-visitor id nobody else shares.
  const { worldId: routeWorldId, listingId: previewListingId } = useParams<{ worldId?: string; listingId?: string }>();
  const preview = !!previewListingId || localBlackthorn || localLastBite || localCarnival;
  const worldId = preview ? undefined : routeWorldId;
  const previewRoom = useMemo(() => (preview ? `preview-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}` : ''), [preview]);
  const [previewListing, setPreviewListing] = useState<PreviewListing | null>(null);
  const navigate = useNavigate();
  const localAvatarRef = useRef<THREE.Group | null>(null);
  // On headsets, offer VR immediately instead of showing the flat scene first
  // (WebXR needs one user gesture, so this is a single-tap splash).
  // (Not in the installed immersive Quest app: it enters VR by itself on launch.)
  const [showVREntry, setShowVREntry] = useState(() => isQuestBrowser && !isQuestImmersiveApp());
  // Entering VR also joins voice, muted. The Enter VR click is a real user
  // gesture, so audio playback unlocks here; inside the headset the menu's
  // mic button then only has to unmute. (Before, voice needed the 2D "Join
  // Voice" button, which Quest's one-tap splash skips entirely.)
  const enterVR = () => {
    requestVoiceJoin({ muted: true });
    unlockVoiceAudio();
    xrStore.enterVR();
  };
  // True when the signed-in user owns this world (enables thumbnail capture)
  const [isOwner, setIsOwner] = useState(false);
  // GathrXR Home (settings.vr_home): draws the dashboard instead of a sky.
  const [vrHome, setVrHome] = useState(() => isVRHomeWorld(worldId));

  // Marble worlds live in local state (same pattern as WorldBuilder)
  const [marbleWorlds, setMarbleWorlds] = useState<Array<{
    id: string;
    name: string;
    splatUrl: string;
    radUrl?: string | null;
    colliderUrl: string | null;
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
    groundPlaneOffset: number | null;
  }>>([]);

  const [loading, setLoading] = useState(true);
  // Came through another world's portal: step out beside the one leading back.
  useWorldPortalArrival(!loading);
  const [loadError, setLoadError] = useState<string | null>(null);
  // A private world this visitor isn't a member of: ask for the access code
  // instead of failing. Joining bumps reloadKey to load it for real.
  const [privateGate, setPrivateGate] = useState<{ signedIn: boolean } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isInVR, setIsInVR] = useState(false);
  // The page decides whether you play as an avatar (local state, set once the
  // saved / wardrobe avatar is known); after that the store's avatar wins, so a
  // switch (services/avatarSwitch) or a freshly created avatar re-renders the
  // player in the same update that changes what MultiplayerSync broadcasts.
  const [pageAvatarUrl, setAvatarUrl] = useState<string | null>(null);
  const storeAvatarUrl = useWorldStore((s) => s.avatarUrl);
  const avatarUrl = pageAvatarUrl && storeAvatarUrl && storeAvatarUrl !== 'skip' ? storeAvatarUrl : pageAvatarUrl;
  const [userName, setUserName] = useState('');
  // Voice tokens require an account, so guests don't get a Join Voice button.
  const [signedIn, setSignedIn] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showAvatarCreator, setShowAvatarCreator] = useState(false);
  // "Get a new avatar" from the in-world wardrobe opens the same create flow.
  const avatarCreateOpen = useAvatarSwitch((s) => s.createOpen);
  const [isPointerLocked, setIsPointerLocked] = useState(false);
  const [avatarToastDismissed, setAvatarToastDismissed] = useState(preview); // previews skip the avatar prompt (joystick shows at once)
  // Phones / narrow windows: one bottom bar instead of the scattered desktop HUD.
  const compactHud = useCompactHud();
  const [emotesOpen, setEmotesOpen] = useState(false);
  // Desktop third-person camera mode (only relevant when the visitor has an avatar)
  const [desktopCamMode, setDesktopCamMode] = useState<DesktopCamMode>('normal');
  // Splat worlds (e.g. the Nexus) spawn you inside the splat, but the wide
  // "normal" camera sits outside the shell — so start closer ("zoom") when a
  // splat is present so the visitor comes in already inside. Skip if the user
  // already picked a camera mode themselves.
  const camUserSet = useRef(false);
  useEffect(() => {
    if (marbleWorlds.length > 0 && !camUserSet.current) setDesktopCamMode('zoom');
  }, [marbleWorlds]);

  // Whether the visitor plays as a visible avatar (third-person) vs the
  // first-person tour fallback.
  const hasAvatar = !!avatarUrl && avatarUrl !== 'skip';

  // Lite rendition: phones cap the pixel ratio (fill rate is their limit).
  // Cleared on leaving the viewer so the editor (same SPA) never sees lite URLs.
  useEffect(() => () => useLiteStore.getState().setLite(null), []);
  const liteActive = useLiteStore((s) => s.active);
  const liteMaxDpr = useLiteStore((s) => s.settings?.maxDpr ?? LITE_DEFAULTS.maxDpr);

  // Read from worldStore (populated by loadWorld)
  const contentItems = useWorldStore((s) => s.contentItems);
  const environmentModels = useWorldStore((s) => s.environmentModels);
  const primitives = useWorldStore((s) => s.primitives);
  const hasArcade = useMemo(() => Object.values(primitives).some((p) => p.shape === 'arcade' && p.visible !== false), [primitives]);
  const hasBlackthorn = useMemo(() => Object.values(primitives).some((p) => p.shape === 'worldgame' && p.worldGame?.game === 'blackthorn-escape' && p.worldGame.role === 'start' && p.visible !== false), [primitives]);
  const hasLastBite = useMemo(() => Object.values(primitives).some((p) => p.shape === 'worldgame' && p.worldGame?.game === 'last-bite' && p.worldGame.role === 'start' && p.visible !== false), [primitives]);
  /** A built-in game world (own sky, fog and lights): skip the world's sky and atmosphere. */
  const hasCarnival = useMemo(() => Object.values(primitives).some((p) => p.shape === 'worldgame' && p.worldGame?.game === 'giggles-carnival' && p.worldGame.role === 'start' && p.visible !== false), [primitives]);
  const hasBuiltInGame = hasBlackthorn || hasLastBite || hasCarnival;
  const hasWorldGame = useMemo(() => Object.values(primitives).some((p) => p.shape === 'worldgame' && p.worldGame?.game !== 'blackthorn-escape' && p.worldGame?.game !== 'last-bite' && p.worldGame?.game !== 'giggles-carnival' && p.visible !== false), [primitives]);
  // NPCs with conversation on (for the "Talk to …" prompt).
  // NPC conversations cost real AI calls and need the world's room: off in previews.
  const talkingNpcIds = React.useMemo(
    () => preview ? [] : Object.entries(primitives).filter(([, p]) => p.shape === 'npc' && p.visible !== false && p.npc?.talk?.enabled && p.npc.status === 'ready').map(([id]) => id),
    [primitives, preview],
  );
  const lights = useWorldStore((s) => s.lights);
  const screenProps = useWorldStore((s) => s.screenProps);
  const skyboxes = useWorldStore((s) => s.skyboxes);
  const currentSkyboxId = useWorldStore((s) => s.currentSkyboxId);
  const groundSettings = useWorldStore((s) => s.groundSettings);
  const groundVisible = useWorldStore((s) => s.groundVisible);
  const spawnPosition = useWorldStore((s) => s.spawnPosition);
  const liveScreenShare = useWorldStore((s) => s.activeScreenShare);

  // Shared room screens (cloud browser / synced video) for this world.
  useEffect(() => {
    if (!worldId) return;
    return initRoomScreens(worldId);
  }, [worldId]);
  // ?sharepanel=1 renders the VR share panel + keyboard in front of the
  // desktop camera, so it can be tested and screenshotted without a headset.
  const sharePanelPreview = new URLSearchParams(window.location.search).has('sharepanel');

  // Track VR session for hiding HTML overlay
  useEffect(() => {
    const unsub = xrStore.subscribe((state: any) => setIsInVR(!!state.session));
    return unsub;
  }, []);

  // ?xr=auto in a normal browser: best-effort VR entry without the tap (the
  // browser may refuse without a gesture; then the one-tap splash stays up).
  // The installed immersive Quest app doesn't use this: it requests its
  // session before React even renders (utils/questImmersiveSession) and
  // <QuestImmersiveBridge/> hands it to three.js.
  useEffect(() => {
    if (isQuestImmersiveApp()) return;
    if (new URLSearchParams(window.location.search).get('xr') !== 'auto' || !navigator.xr) return;
    let tries = 0;
    let busy = false;
    const id = window.setInterval(() => {
      if (xrStore.getState().session || ++tries > 12) { window.clearInterval(id); return; }
      if (busy) return;
      busy = true;
      requestVoiceJoin({ muted: true });
      unlockVoiceAudio();
      xrStore.enterVR()
        .then(() => setShowVREntry(false))
        .catch((err: unknown) => console.warn('[xr=auto] enterVR failed', err))
        .finally(() => { busy = false; });
    }, 1500);
    return () => window.clearInterval(id);
  }, []);

  // Load avatar + username from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('eb-avatar-url');
    if (saved && !saved.includes('readyplayer.me') && !saved.includes('d1a370nemizbjq.cloudfront.net')) {
      setAvatarUrl(saved);
      // Also push into the store so MultiplayerSync broadcasts it — this is what
      // lets other players see this visitor's avatar (and their emotes).
      useWorldStore.getState().setAvatarUrl(saved);
    }
    // Name: stored/generated immediately, upgraded to the profile username for
    // signed-in users. Must also go into the store — chat and presence read it
    // from there, not from this component's local state.
    const applyName = (n: string) => { setUserName(n); useWorldStore.getState().setUserName(n); };
    applyName(getStoredUserName());
    const loadSignedInUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        // Multiplayer packets carry the store's user id (tab dedupe, roles, knocks).
        useWorldStore.getState().setUser(user);
        setSignedIn(true);
        try {
          const activeAvatar = await getActiveAvatar();
          if (activeAvatar?.model_url) {
            setAvatarUrl(activeAvatar.model_url);
            useWorldStore.getState().setAvatarFromDb(activeAvatar);
          }
        } catch (err) {
          console.warn('[Avatar] Could not load active wardrobe avatar:', err);
        }
        const { data: prof } = await supabase.from('profiles').select('username').eq('id', user.id).maybeSingle();
        if (prof?.username) applyName(prof.username);
      } catch { /* keep stored name */ }
    };
    void loadSignedInUser();
    // The immersive Quest app signs in from inside VR (QuestVRAccountPanel),
    // without a page load: pick the account up (voice, avatar, name) live.
    // In a regular browser the same panel's code sign-in can do it too (VR
    // Home's account chip); there only a new account is picked up, so a
    // repeated SIGNED_IN for the same user (tab refocus) changes nothing.
    const questApp = isQuestImmersiveApp();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        if (questApp || (session?.user?.id && session.user.id !== useWorldStore.getState().user?.id)) void loadSignedInUser();
      } else if (event === 'SIGNED_OUT' && questApp) { setSignedIn(false); useWorldStore.getState().setUser(null); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Load world data
  useEffect(() => {
    if (!worldId && !previewListingId && !localBlackthorn && !localLastBite && !localCarnival) return;

    let cancelled = false;

    let gated = false;
    const loadWorld = async () => {
      // Always use getState() inside async effects to avoid stale closures
      const s = useWorldStore.getState();
      s.resetWorldData();
      s.setWorldLoading(true);
      s.setLoadingMessage('Loading world');
      useLiteStore.getState().setLite(null);
      // Lite rendition (headsets + phones) when the world ships one: see utils/qualityTier.
      let liteUrls: Record<string, string> | undefined;

      try {
        // Preview: one sanitised payload from the listing-scoped endpoint
        // (scene settings + assets only; no id, owner or access code).
        let previewData: { listing: PreviewListing | null; world: any; assets: any[] } | null = null;
        if (localBlackthorn) {
          const { default: estate } = await import('../components/games/blackthorn/blackthornWorld.json');
          if (cancelled) return;
          previewData = { listing: null, ...estate };
          setPreviewListing(null);
        } else if (localLastBite) {
          const { default: street } = await import('../components/games/lastbite/lastBiteWorld.json');
          if (cancelled) return;
          previewData = { listing: null, ...street };
          setPreviewListing(null);
        } else if (localCarnival) {
          const { default: carnival } = await import('../components/games/carnival/carnivalWorld.json');
          if (cancelled) return;
          previewData = { listing: null, ...carnival };
          setPreviewListing(null);
        } else if (preview) {
          const res = await fetch(`/api/marketplace/preview?listingId=${encodeURIComponent(previewListingId!)}`);
          if (cancelled) return;
          if (!res.ok) throw new Error('Preview unavailable');
          previewData = await res.json();
          if (cancelled) return;
          setPreviewListing(previewData!.listing);
        }
        const { data: world, error: worldError } = previewData
          ? { data: previewData.world, error: null }
          : await supabase.from('worlds').select('*').eq('id', worldId).maybeSingle();
        if (cancelled) return;
        if (worldError) throw worldError;
        if (!world) {
          // Hidden by RLS (private, not a member) or gone. Only the former gets the code prompt.
          const state = await getWorldAccessState(worldId!);
          if (cancelled) return;
          if (state === 'private') {
            const { data: { session } } = await supabase.auth.getSession();
            if (cancelled) return;
            setPrivateGate({ signedIn: !!session });
            gated = true; // stay in the loading state so the HUD/multiplayer don't start behind the prompt
            return;
          }
          throw new Error(state === 'missing' ? 'World not found' : 'World unavailable');
        }
        setPrivateGate(null);
        setLoadError(null);

        if (world) {
          const tier = detectQualityTier(browserTierEnv(isQuestImmersiveApp()));
          const lite = tier === 'lite' ? parseLiteSettings(world.settings?.lite) : null;
          liteUrls = lite?.urls;
          useLiteStore.getState().setLite(lite);
          if (world.settings?.lite) console.log(`[VRViewer] quality tier: ${tier}${lite ? ' (lite rendition)' : ''}`);
          const ws = applyLiteUrls(world.settings || {}, liteUrls);
          useWorldStore.getState().setWorldName(world.name || 'GathrXR');
          setVrHome(!previewData && isVRHomeWorld(worldId, world.settings));
          const g = ws.ground;
          if (g) {
            useWorldStore.getState().setGroundSettings({
              size: g.size ?? 100,
              color: g.color ?? '#1a1a1a',
              textureUrl: g.textureUrl ?? null,
              texturePreset: g.texturePreset ?? null,
              textureScale: g.textureScale ?? 10,
              pbrMaps: g.pbrMaps ?? null,
              opacity: g.opacity ?? 1,
              showGrid: g.showGrid ?? true,
              isLocked: false,
            });
            useWorldStore.getState().setGroundVisible(g.visible ?? true);
          }
          useWorldStore.getState().setAtmosphere({ areaParticles: null, shadowRadius: undefined, ...(ws.atmosphere || {}) });
          if (world.settings?.spawnPosition) useWorldStore.getState().setSpawnPosition(world.settings.spawnPosition);
          useWorldStore.getState().setWorldBounds(parseWorldBounds(world.settings?.bounds));
          useWorldStore.getState().setWorldMusic(parseWorldMusic(world.settings?.music));
          // Owner check — gates the "Set as Thumbnail" HUD button.
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!cancelled) setIsOwner(!!user && user.id === world.owner_id);
          } catch { /* not signed in */ }
        }

        const { data: rawAssets, error: assetsError } = previewData
          ? { data: previewData.assets, error: null }
          : await supabase.from('world_assets').select('*').eq('world_id', worldId);
        if (cancelled) return;
        if (assetsError) throw assetsError;
        const assets = applyLiteUrls(rawAssets, liteUrls);

        if (assets && assets.length > 0) {
          useWorldStore.getState().loadWorldAssets(assets);
          const restoredMarble = assets
            .filter((a: any) => a.type === 'marble_world')
            .map((a: any, i: number) => ({
              id: `marble-${i}`,
              name: a.metadata?.name || 'AI World',
              splatUrl: a.url,
              radUrl: a.metadata?.radUrl || null,
              colliderUrl: a.metadata?.colliderUrl || null,
              position: a.metadata?.position || [0, 0, 0] as [number, number, number],
              rotation: a.metadata?.rotation || [0, 0, 0] as [number, number, number],
              scale: a.metadata?.scale || [1, 1, 1] as [number, number, number],
              groundPlaneOffset: a.metadata?.groundPlaneOffset || null,
            }));
          if (restoredMarble.length > 0) setMarbleWorlds(restoredMarble);
        }

        console.log(`[VRViewer] World loaded: ${worldId}`);
      } catch (err) {
        if (cancelled) return;
        console.error('[VRViewer] Load failed:', err);
        setLoadError(err instanceof Error && err.message === 'Preview unavailable'
          ? 'This preview isn\u2019t available \u2014 the listing may have sold or been taken down.'
          : err instanceof Error && err.message === 'World not found'
          ? 'This world doesn\u2019t exist or was deleted.'
          : 'Failed to load world.');
      } finally {
        if (!cancelled && !gated) {
          setLoading(false);
          clearWorldHistory();
          useWorldStore.getState().setWorldLoading(false);
        }
      }
    };

    loadWorld();

    return () => {
      cancelled = true;
    };
  }, [worldId, previewListingId, localBlackthorn, localLastBite, localCarnival, reloadKey]);

  return (
    <div className={`gx-viewer${compactHud ? ' gx-viewer--compact' : ''}${seatedAtGame ? ' gx-viewer--gaming' : ''}`} style={{ width: '100vw', background: '#000', position: 'relative', overflow: 'hidden' }}>
      {!preview && <MediaPanelDialogs worldId={worldId || ''} canConfigure={isOwner} />}
      {/* One-tap VR entry splash on headsets — skips the flat browser preview */}
      {showVREntry && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 2000,
          background: 'radial-gradient(ellipse at 50% 35%, #0c1626 0%, #060e18 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18,
        }}>
          <div style={{ fontSize: 30, fontWeight: 800, color: '#eaf6f8', fontFamily: 'Outfit, sans-serif' }}>
            Gathr<span style={{ color: '#00c2b3' }}>XR</span>
          </div>
          <div style={{ fontSize: 15, color: 'rgba(234,246,248,0.6)', fontFamily: 'Outfit, sans-serif' }}>
            {new URLSearchParams(window.location.search).get('name') || 'Your world'} is ready
          </div>
          <button
            onClick={() => { setShowVREntry(false); enterVR(); }}
            style={{
              padding: '16px 44px', fontSize: 18, fontWeight: 700, color: '#fff',
              background: 'linear-gradient(135deg, #00c2b3, #0077b6)', border: 'none',
              borderRadius: 999, cursor: 'pointer', fontFamily: 'Outfit, sans-serif',
              boxShadow: '0 12px 40px rgba(0,194,179,0.35)',
            }}
          >
            Enter VR
          </button>
          <button
            onClick={() => setShowVREntry(false)}
            style={{
              background: 'none', border: 'none', color: 'rgba(234,246,248,0.4)',
              fontSize: 13, cursor: 'pointer', fontFamily: 'Outfit, sans-serif', textDecoration: 'underline',
            }}
          >
            Continue in browser
          </button>
        </div>
      )}
      {/* 3D Canvas */}
      <Canvas
        shadows
        // Immersive Quest app: nothing is composited before the XR session, so
        // ResizeObserver never fires; measure by hand and never let it be 0x0.
        style={isQuestImmersiveApp() ? { position: 'absolute', inset: 0, minWidth: 1280, minHeight: 720 } : { position: 'absolute', inset: 0 }}
        resize={isQuestImmersiveApp() ? { polyfill: QuestResizeObserver as any } : undefined}
        gl={{
          antialias: !isQuestBrowser,
          powerPreference: 'high-performance',
          alpha: false,
          preserveDrawingBuffer: !isQuestBrowser, // desktop: enables screenshot/recording
          failIfMajorPerformanceCaveat: false,
        }}
        dpr={isQuestBrowser ? 1 : liteActive ? [1, liteMaxDpr] : [1, 2]}
        camera={{ fov: 60, near: 0.1, far: 1000 }}
        onCreated={({ gl, scene, camera }) => {
          // Shadows cost a depth pass per light: desktop only, and only for
          // worlds whose sun casts shadows (AtmosphereScene sets that per world).
          gl.shadowMap.enabled = !isQuestBrowser;
          gl.shadowMap.type = THREE.PCFSoftShadowMap;
          // Dev only: lets the headless test harness frame shots and measure
          // the scene (room-screen placement). Stripped from production builds.
          if (import.meta.env.DEV) (window as any).__gathrxrThree = { gl, scene, camera, store: useWorldStore, xr: xrStore };
          // Attempt to recover from context loss (GPU memory pressure / rapid nav)
          gl.domElement.addEventListener('webglcontextlost', (e) => {
            e.preventDefault(); // allow restore attempt
            console.warn('[VRViewer] WebGL context lost — attempting restore');
          });
          gl.domElement.addEventListener('webglcontextrestored', () => {
            console.log('[VRViewer] WebGL context restored');
          });
        }}
      >
        <KTX2Init />
        <XR store={xrStore}>
          <QuestImmersiveBridge />
          <QuestVRAccountPanel />
          <VRBackgroundEnforcer />
          <VRPreloader />
          <VRFrameOptimizer />
          <VRPerformanceManager>

            <Suspense fallback={null}>
              {/* Sky / Skybox */}
              {vrHome ? <SafeR3F name="vr-home"><Suspense fallback={null}><VRHomeDashboard worldId={worldId} /></Suspense></SafeR3F>
                : hasBuiltInGame ? <color attach="background" args={[hasLastBite ? '#0d1617' : hasCarnival ? '#070b09' : '#101920']} /> : currentSkyboxId && skyboxes[currentSkyboxId] ? (
                <SafeR3F name="skybox"><Environment files={skyboxes[currentSkyboxId].url} background /></SafeR3F>
              ) : (
                <>
                  <Sky distance={450000} sunPosition={[100, 20, 100]} inclination={0} azimuth={0.25} />
                  <SafeR3F name="env-hdri"><Environment files="/hdri/venice_sunset_1k.hdr" /></SafeR3F>
                </>
              )}

              {/* Atmosphere (fog, bloom, particles) */}
              {!hasBuiltInGame && <AtmosphereScene />}

              {/* Ground — full texture support (preset / PBR maps / custom URL),
                  same renderer as the builder. Color-only fallback while the
                  texture loads; wrapped so a texture failure can't crash the world. */}
              <SafeR3F name="ground">
                <Suspense fallback={<ViewerGround visible={groundVisible} size={groundSettings.size} color={groundSettings.color} y={spawnPosition[1]} opacity={groundSettings.opacity ?? 1} />}>
                  <group position={[0, spawnPosition[1], 0]}>
                    <Ground
                      visible={groundVisible}
                      size={groundSettings.size}
                      color={groundSettings.color}
                      textureUrl={groundSettings.textureUrl || null}
                      texturePreset={groundSettings.texturePreset || null}
                      pbrMaps={groundSettings.pbrMaps || null}
                      textureScale={groundSettings.textureScale || 4}
                      opacity={groundSettings.opacity ?? 1}
                    />
                  </group>
                </Suspense>
              </SafeR3F>

              {/* Environment models */}
              {Object.entries(environmentModels).map(([id, model]) =>
                model.visible && (
                  <SimpleEnvironmentModel
                    key={id}
                    url={model.url}
                    position={model.transform.position}
                    scale={model.transform.scale}
                    rotation={model.transform.rotation}
                    collision={model.collision}
                    isSelected={false}
                  />
                )
              )}

              {/* Content items (images, models, splats) */}
              {Object.entries(contentItems).map(([id, item]) =>
                item.visible && (
                  <SkipOnError key={id} name={`content-${id}`}>
                    <ContentDisplay
                      content={{ id, ...item }}
                      isSelected={false}
                      onSelect={() => {}}
                    />
                  </SkipOnError>
                )
              )}

              {/* Marble / AI worlds */}
              {marbleWorlds.map((mw) => (
                <MarbleWorldRenderer
                  key={mw.id}
                  splatUrl={mw.splatUrl}
                  radUrl={mw.radUrl}
                  colliderUrl={mw.colliderUrl}
                  position={mw.position}
                  rotation={mw.rotation}
                  scale={mw.scale}
                  groundPlaneOffset={mw.groundPlaneOffset}
                />
              ))}

              {/* Placed displays remain usable without entering Build. */}
              {Object.entries(screenProps).map(([id, prop]) => prop.visible && (prop.roomScreen ? (
                <group key={id} position={prop.position} rotation={prop.rotation} scale={prop.scale}>
                  <RoomScreen id={id} item={prop} isOwner={isOwner} />
                </group>
              ) : (
                <group key={id} position={prop.position} rotation={prop.rotation} scale={prop.scale}>
                  {prop.type === 'browser' ? <BrowserProp position={[0, 0, 0]} rotation={[0, 0, 0]} scale={1} url={prop.browserUrl || ''} /> :
                    <ScreenProp position={[0, 0, 0]} rotation={[0, 0, 0]} scale={1} screenType={prop.type} videoUrl={prop.videoUrl} streamData={prop.streamData || (prop.type === 'screen-share' ? liveScreenShare : null)} />}
                  <MediaPanelActions id={id} item={prop} canConfigure={isOwner} />
                </group>
              )))}
              {sharePanelPreview && <VRSharePanelPreview isOwner={isOwner} />}

              {/* Primitives */}
              {Object.entries(primitives).map(([id, prim]) => (
                prim.shape === 'portal' ? <ViewerPortal key={id} id={id} primitive={prim} />
                  : prim.shape === 'gametable' ? <ViewerGameTable key={id} id={id} primitive={prim} worldId={worldId || previewRoom} avatarRef={localAvatarRef} />
                  : prim.shape === 'leaderboard' ? (prim.visible !== false ? (
                    <Suspense key={id} fallback={null}><ViewerLeaderboard primitive={prim} worldId={worldId || previewRoom} /></Suspense>
                  ) : null)
                  : prim.shape === 'arcade' ? (prim.visible !== false ? (
                    <Suspense key={id} fallback={null}><ViewerArcadeCabinet id={id} primitive={prim} worldId={worldId || previewRoom} avatarRef={localAvatarRef} /></Suspense>
                  ) : null)
                  : prim.shape === 'speaker' ? (prim.visible !== false ? <ViewerSpeaker key={id} id={id} primitive={prim} /> : null)
                  : prim.shape === 'worldgame' ? null /* drawn by WorldGameRuntime below */
                  : prim.shape === 'npc' ? (prim.npc && prim.visible !== false ? (
                    <group key={id} position={[prim.position[0], prim.position[1] - prim.scale[1] * 0.9, prim.position[2]]} rotation={prim.rotation} scale={prim.scale}>
                      <NpcAvatar id={id} npc={prim.npc} />
                    </group>
                  ) : null)
                  : <ViewerPrimitive key={id} primitive={prim} />
              ))}

              {hasBlackthorn && <Suspense fallback={null}><BlackthornRuntime worldId={worldId || previewRoom} avatarRef={localAvatarRef} /></Suspense>}
              {hasLastBite && <Suspense fallback={null}><LastBiteRuntime worldId={worldId || previewRoom} avatarRef={localAvatarRef} /></Suspense>}
              {hasCarnival && <Suspense fallback={null}><CarnivalRuntime worldId={worldId || previewRoom} avatarRef={localAvatarRef} /></Suspense>}
              {hasWorldGame && <Suspense fallback={null}><WorldGameRuntime worldId={worldId || previewRoom} avatarRef={localAvatarRef} /></Suspense>}

              {/* Lights */}
              {Object.entries(lights).map(([id, light]) => (
                <ViewerLight key={id} light={light} />
              ))}

            </Suspense>

            {/* VR player rig — locomotion, avatar body, collision.
                Each loader gets its own boundary: a suspended avatar must not
                hold back MultiplayerSync, or the player isn't announced (and
                can't see anyone) until every model has loaded. */}
            <Suspense fallback={null}>
              <ViewerVRRig avatarUrl={avatarUrl && avatarUrl !== 'skip' ? avatarUrl : undefined} worldId={worldId || previewRoom} isOwner={isOwner} home={vrHome} />
            </Suspense>


            {/* Desktop player. With an avatar → third-person play mode (you SEE
                yourself, camera follows, WASD move, drag to orbit, scroll to zoom,
                emotes). Without one → the original first-person tour camera.
                Exactly one branch feeds localAvatarRef, so MultiplayerSync always
                has a single position source. Returns null in VR (the XR rig owns
                the body). */}
            <Suspense fallback={null}>
              <DesktopViewerPlayer
                avatarUrl={avatarUrl}
                userName={userName}
                spawnPosition={spawnPosition}
                ready={!loading}
                cameraMode={desktopCamMode}
                onLockChange={setIsPointerLocked}
                localAvatarRef={localAvatarRef}
              />
            </Suspense>

            {/* Remote players */}
            {/* Not while a private world's code prompt is up: no presence before joining. */}
            {/* No presence in previews: a buyer trying a world must not appear in (or see) the seller's live room. */}
            {!privateGate && !preview && <MultiplayerSync worldId={worldId || 'default'} localAvatarRef={localAvatarRef} />}
            <Suspense fallback={null}><MusicListenerFrame /></Suspense>
            {talkingNpcIds.length > 0 && <NpcTalkProximity ids={talkingNpcIds} avatarRef={localAvatarRef} />}
            {talkingNpcIds.length > 0 && worldId && <Suspense fallback={null}><NpcTalkVR worldId={worldId} primitives={primitives} /></Suspense>}
            <PortalTraveler avatarRef={localAvatarRef} />
            {isTouchDevice && <TouchLook />}

          </VRPerformanceManager>
        </XR>
      </Canvas>

      {/* HTML overlay — hidden in VR (panels appear as black rectangles on Quest) */}
      {!isInVR && (
        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Loading screen */}
          {loading && (
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(5,14,24,0.95)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              pointerEvents: 'all',
              zIndex: 100,
            }}>
              <div style={{ width: 40, height: 40, border: '3px solid rgba(0,194,179,0.3)', borderTop: '3px solid #00c2b3', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <p style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'sans-serif', fontSize: 14 }}>Loading world…</p>
            </div>
          )}

          {privateGate && worldId && (
            <PrivateWorldGate
              variant="page"
              worldId={worldId}
              worldName={new URLSearchParams(window.location.search).get('name') || undefined}
              initialCode={new URLSearchParams(window.location.search).get('code') || undefined}
              signedIn={privateGate.signedIn}
              onJoined={() => {
                // Drop ?code= from the address bar now that it has done its job.
                const sp = new URLSearchParams(window.location.search);
                if (sp.has('code')) {
                  sp.delete('code');
                  const q = sp.toString();
                  window.history.replaceState(window.history.state, '', window.location.pathname + (q ? `?${q}` : ''));
                }
                setPrivateGate(null);
                setLoading(true);
                setReloadKey((k) => k + 1);
              }}
              onCancel={() => navigate('/dashboard')}
              cancelLabel="Back to dashboard"
            />
          )}

          {loadError && (
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(5,14,24,0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'all',
              zIndex: 100,
            }}>
              <p style={{ color: '#ef4444', fontFamily: 'sans-serif', fontSize: 14 }}>{loadError}</p>
            </div>
          )}

          {/* Top bar — Back + Chat buttons (compact HUD: on the bottom bar) */}
          {!compactHud && <div style={{
            position: 'absolute',
            top: 20,
            left: 20,
            pointerEvents: 'all',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}>
            <button
              onClick={() => navigate(preview ? (previewListing ? `/marketplace?item=${previewListing.id}` : '/marketplace') : '/dashboard')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                background: 'rgba(5,14,24,0.75)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                color: 'rgba(255,255,255,0.8)',
                fontFamily: 'sans-serif',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(5,14,24,0.95)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.3)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(5,14,24,0.75)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.15)'; }}
            >
              ← Leave
            </button>
            {!preview && <button
              onClick={() => setShowChat((v) => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: showChat ? 'rgba(0,194,179,0.18)' : 'rgba(5,14,24,0.75)',
                border: showChat ? '1px solid rgba(0,194,179,0.5)' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                color: showChat ? '#00c2b3' : 'rgba(255,255,255,0.8)',
                fontFamily: 'sans-serif',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { if (!showChat) { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(5,14,24,0.95)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.3)'; } }}
              onMouseLeave={e => { if (!showChat) { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(5,14,24,0.75)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.15)'; } }}
            >
              <MessageSquare size={14} />
              Chat
            </button>}
          </div>}

          {/* Click-to-explore hint — only for pointer-lock control (first-person
              tour, or first-person avatar camera). Third-person orbit uses
              mouse-drag, so the "click to look" prompt would be misleading. */}
          {!loading && !isPointerLocked && !isInVR && !isTouchDevice && (!hasAvatar || desktopCamMode === 'firstPerson') && (
            <div className="gx-look-hint" style={{
              position: 'absolute',
              bottom: 90,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(5,14,24,0.8)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10,
              padding: '10px 20px',
              color: 'rgba(255,255,255,0.65)',
              fontFamily: 'sans-serif',
              fontSize: 13,
              backdropFilter: 'blur(8px)',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}>
              Click to look around · WASD to move
            </div>
          )}

          {/* Avatar gate — blocking: force a choice (create an avatar, or
              explicitly continue in view-only). No click-away dismissal. */}
          {/* Not in previews: nobody else is there to see you, so just look around. */}
          {(!avatarUrl || avatarUrl === 'skip') && !loading && !avatarToastDismissed && !isInVR && !preview && (
            <div style={{
              position: 'absolute',
              inset: 0,
              zIndex: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              background: 'rgba(3,8,15,0.72)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              pointerEvents: 'all',
            }}>
              <div style={{
                width: 'min(92vw, 420px)',
                background: 'rgba(8,16,28,0.97)',
                border: '1px solid rgba(0,194,179,0.28)',
                borderRadius: 18,
                padding: '30px 26px',
                textAlign: 'center',
                boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
                fontFamily: 'Outfit, sans-serif',
              }}>
                <div style={{
                  width: 58, height: 58, margin: '0 auto 16px', borderRadius: '50%',
                  background: 'rgba(0,194,179,0.12)', border: '1px solid rgba(0,194,179,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#3fecdb',
                }}><UserRound size={26} /></div>
                <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'rgba(234,246,248,0.96)' }}>
                  Choose how you'll appear
                </h2>
                <p style={{ margin: '0 0 22px', fontSize: 14, lineHeight: 1.55, color: 'rgba(234,246,248,0.6)' }}>
                  Create an avatar to walk around as yourself and be seen by others — or explore this world in view-only mode.
                </p>
                <button
                  onClick={() => setShowAvatarCreator(true)}
                  style={{
                    width: '100%', padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                    fontSize: 15, fontWeight: 700, fontFamily: 'Outfit, sans-serif', color: '#fff',
                    background: 'linear-gradient(135deg, #00c2b3, #0077b6)',
                    boxShadow: '0 10px 28px rgba(0,194,179,0.28)', marginBottom: 10,
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                    <Sparkles size={16} /> Create your avatar
                  </span>
                </button>
                <button
                  onClick={() => setAvatarToastDismissed(true)}
                  style={{
                    width: '100%', padding: '11px', borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.14)', background: 'transparent', cursor: 'pointer',
                    fontSize: 13.5, fontWeight: 600, fontFamily: 'Outfit, sans-serif', color: 'rgba(234,246,248,0.6)',
                  }}
                >
                  Enter in view-only mode
                </button>
              </div>
            </div>
          )}

          {/* VR entry button — bottom right (compact HUD: in the More sheet) */}
          {!loading && !compactHud && (
            <div style={{
              position: 'absolute',
              bottom: 24,
              right: 24,
              pointerEvents: 'all',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 10,
            }}>
              <VRButtonComponent onEnterVR={enterVR} />
              <p style={{
                color: 'rgba(255,255,255,0.35)',
                fontFamily: 'sans-serif',
                fontSize: 11,
                margin: 0,
              }}>
                {avatarUrl && avatarUrl !== 'skip' ? 'Play mode' : 'View-only mode'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Room screen controls (nearest screen), desktop only */}
      {!loading && !isInVR && <RoomScreenDock />}

      {/* Voice chat (outside canvas, always mounted) */}
      {!loading && worldId && signedIn && (
        <VoiceChatWrapper roomName={worldId} userName={userName} minimal={true} listenOnly={false} />
      )}

      {/* Avatar switching: "Your avatars" panel + toast (VR draws its own panel) */}
      {!loading && <AvatarSwitchLayer inVR={isInVR || sharePanelPreview} />}

      {/* World hopping: picker panel, invite prompt (listens on the room channel) */}
      {!loading && worldId && <WorldHopLayer worldId={worldId} listen={!privateGate} inVR={isInVR} />}

      {/* Chat overlay */}
      {showChat && worldId && <ChatOverlay worldId={worldId} onClose={() => setShowChat(false)} />}

      {/* Desktop tutorial + capture buttons */}
      {!loading && <DesktopViewerHUD worldId={worldId} isOwner={isOwner} bar={compactHud && !isInVR ? {
        onBack: () => navigate(preview ? '/marketplace' : '/dashboard'),
        chatOpen: showChat,
        onToggleChat: preview ? undefined : () => setShowChat((v) => !v),
        hasAvatar,
        emotesOpen,
        onToggleEmotes: () => setEmotesOpen((v) => !v),
        camMode: desktopCamMode,
        onCamMode: (mode) => { camUserSet.current = true; setDesktopCamMode(mode); },
        modeLabel: hasAvatar ? 'Play mode' : 'View-only mode',
        sheetExtra: <VRButtonComponent onEnterVR={enterVR} />,
      } : undefined} />}

      {/* Emote wheel — desktop only, only when the visitor plays as an avatar.
          Fires the local emote and broadcasts it to everyone in the world. */}
      {!loading && !isInVR && hasAvatar && worldId && (
        <EmoteWheel
          open={compactHud ? emotesOpen : undefined}
          onOpenChange={setEmotesOpen}
          onEmote={(name) => {
            useWorldStore.getState().playLocalEmote(name);
            broadcastEmote(worldId, name);
          }}
        />
      )}

      {/* Phones / tablets: on-screen joystick (drag the view to look, pinch to zoom). */}
      {isTouchDevice && !loading && !isInVR && !showChat && !seatedAtGame && (hasAvatar || avatarToastDismissed) && <TouchJoystick />}
      {!loading && !isInVR && <GameOverlay />}
      {!loading && !privateGate && (
        <Suspense fallback={null}>
          <MusicRuntime worldId={worldId || null} />
          {!isInVR && <MusicOverlay worldId={worldId || null} />}
        </Suspense>
      )}
      {!loading && !isInVR && hasArcade && <Suspense fallback={null}><ArcadeOverlay worldId={worldId || previewRoom} /></Suspense>}
      {!loading && !isInVR && hasBlackthorn && <Suspense fallback={null}><BlackthornOverlay /></Suspense>}
      {!loading && !isInVR && hasLastBite && <Suspense fallback={null}><LastBiteOverlay /></Suspense>}
      {!loading && !isInVR && hasCarnival && <Suspense fallback={null}><CarnivalOverlay /></Suspense>}
      {!loading && !isInVR && hasWorldGame && <Suspense fallback={null}><WorldGameOverlay worldId={worldId || previewRoom} /></Suspense>}

      {/* "Try it" banner: what this is, and the way to buy it. */}
      {preview && previewListing && !isInVR && <PreviewBanner listing={previewListing} />}
      {!loading && !isInVR && talkingNpcIds.length > 0 && worldId && <NpcTalkPanel worldId={worldId} primitives={primitives} />}

      {/* Camera-mode toggle — desktop third-person only. Normal / Zoom /
          First-person all reuse FixedRetargetedAvatar's own camera props. */}
      {!loading && !isInVR && hasAvatar && !compactHud && (
        <CamPill mode={desktopCamMode} onMode={(mode) => { camUserSet.current = true; setDesktopCamMode(mode); }} />
      )}

      {/* Avatar creator */}
      {(showAvatarCreator || avatarCreateOpen) && !isInVR && (
        <AvatarCreateModal
          variant="more"
          equip="always"
          returnPath="/avatars/new"
          finishLabel="Back to the world"
          onFinish={(avatar, equipped) => {
            if (avatar && equipped) setAvatarUrl(avatar.model_url);
            setShowAvatarCreator(false);
            useAvatarSwitch.getState().setCreateOpen(false);
          }}
          onClose={() => { setShowAvatarCreator(false); useAvatarSwitch.getState().setCreateOpen(false); }}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default VRViewer;
